import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useAlert } from '../contexts/AlertContext'
import MapList from '../components/MapList'
import MapUpload from '../components/MapUpload'
import MapEditor from '../components/MapEditor'
import Topbar from '../components/layout/Topbar'
import Sidebar from '../components/layout/Sidebar'
import { PieChart, LineChart, BarChart, ComboChart } from '../components/charts'
import config from '../config'
import { arrayToCSV, downloadCSV, formatCurrency, formatDate, formatStatus } from '../utils/export'
import { STATUS } from '../utils/status'

function TopLotsByClicks({ mapId, token }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState({ labels: [], clicks: [], enquiries: [], lots: [] })
  const getResponsiveLimit = () => {
    if (typeof window === 'undefined') return 10
    const w = window.innerWidth
    if (w <= 480) return 5      // mobile
    if (w <= 992) return 8      // tablet
    return 10                   // desktop
  }
  const [limit, setLimit] = useState(getResponsiveLimit())

  useEffect(() => {
    const onResize = () => {
      const next = getResponsiveLimit()
      setLimit(prev => (prev !== next ? next : prev))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!mapId) return
    setLoading(true)
    setError(null)
    fetch(config.getApiUrl(`/charts/map/${mapId}/top-lots?limit=${limit}`), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('failed')
        const j = await res.json()
        // Keep raw lot numbers/labels; formatting is handled in the chart tick callback
        setData({ ...j })
      })
      .catch((e) => setError(e?.message || 'error'))
      .finally(() => setLoading(false))
  }, [mapId, token, limit])

  return (
    <BarChart
      data={{
        labels: data.labels,
        datasets: [
          { label: 'Clicks', data: data.clicks },
          { label: 'Enquiries', data: data.enquiries }
        ]
      }}
      title={["Lot Engagement Report", `Clicks vs Total Enquiries for Top ${limit} Lots`]}
      colors={['#001a3d', '#55754e']}
      loading={loading}
        horizontal={false}
      showValueLabels={true}
      valueFormatter={(v)=> typeof v==='number'? v.toLocaleString(): v}
      barGap={0.4}
      categoryGap={0.35}
      minCategoryHeight={44}
    />
  )
}

export default function AdminPage() {
  const { user, logout, token } = useAuth()
  const { alert } = useAlert()
  const [currentView, setCurrentView] = useState('dashboard') // 'dashboard','list','upload','editor','settings','enquiries','lots','lot-details','plan-share'
  const [selectedMap, setSelectedMap] = useState(null)
  const [selectedMapForLots, setSelectedMapForLots] = useState(null)
  const [activeLotTab, setActiveLotTab] = useState('all')
  const [selectedGroupFilter, setSelectedGroupFilter] = useState(null) // { stageName, groupName, subGroupName } or null for all
  const [selectedStage, setSelectedStage] = useState(null) // Selected stage name for stage-based view
  const [lotsPublishedOnly, setLotsPublishedOnly] = useState(false)
  const [onlyLivePlans, setOnlyLivePlans] = useState(false)
  const [maps, setMaps] = useState([])
  const [enquiries, setEnquiries] = useState([])
  const [loading, setLoading] = useState(true)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isSmall, setIsSmall] = useState(false)
  const [tenant, setTenant] = useState(null)
  const [logoUploading, setLogoUploading] = useState(false)
  const [contactEmail, setContactEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [savingContact, setSavingContact] = useState(false)
  const statusColorDefaults = useMemo(() => ({
    AVAILABLE: '#4CAF50',
    SOLD: '#F44336',
    LOCKED: '#FFD700'
  }), [])
  const [statusColorMapId, setStatusColorMapId] = useState('')
  const [statusColorInputs, setStatusColorInputs] = useState(statusColorDefaults)
  const [savingStatusColors, setSavingStatusColors] = useState(false)
  const [stageTemplateMapId, setStageTemplateMapId] = useState('')
  const [stageTemplateInput, setStageTemplateInput] = useState('default')
  const [savingStageTemplate, setSavingStageTemplate] = useState(false)
  const [chartData, setChartData] = useState({
    lotStatus: [],
    enquiriesTimeline: { labels: [], values: [], datasetLabel: 'Enquiries' },
    planPerformance: { labels: [], datasets: [] },
    priceDistribution: []
  })
  const [chartsLoading, setChartsLoading] = useState(false)
  const [planDrilldown, setPlanDrilldown] = useState(null) // { name, data: [available,sold,locked] }
  const [expandedMsgIds, setExpandedMsgIds] = useState(new Set())
  const [expandedLotIds, setExpandedLotIds] = useState(new Set())
  const [enquiryFilter, setEnquiryFilter] = useState('all') // 'all', 'lot', 'general'
  const [editModal, setEditModal] = useState(null) // { map, regionIndex }
  // const [crmSyncStatus, setCrmSyncStatus] = useState({}) // { enquiryId: 'syncing' | 'synced' | 'error' }
  const [crmConnectionStatus, setCrmConnectionStatus] = useState(null) // null | 'testing' | 'connected' | 'error'
  const [exportModal, setExportModal] = useState(null) // { type: 'default' | 'group', selectedGroup: null }
  const [quickEdit, setQuickEdit] = useState(null) // { regionId, stageName, status, group, subGroup }
  const [quickEditSaving, setQuickEditSaving] = useState(false)
  const [creatingGroup, setCreatingGroup] = useState(false) // true when creating new group
  const [newGroupName, setNewGroupName] = useState('')
  const [creatingSubGroup, setCreatingSubGroup] = useState(null) // groupName when creating sub-group
  const [newSubGroupName, setNewSubGroupName] = useState('')
  const [renamingGroup, setRenamingGroup] = useState(null) // { oldName, newName }
  const [renamingSubGroup, setRenamingSubGroup] = useState(null) // { groupName, oldName, newName }
  const [editingGroupColor, setEditingGroupColor] = useState(null) // { groupName, subGroupName, color }
  const [colorUpdateTrigger, setColorUpdateTrigger] = useState(0)
  const [unassignedGroups, setUnassignedGroups] = useState(new Set()) // Set of group names not yet assigned to any lot
  const [unassignedSubGroups, setUnassignedSubGroups] = useState(new Map()) // Map<groupName, Set<subGroupName>>
  const [searchTerm, setSearchTerm] = useState('') // Search term for filtering lots
  const statusOptions = [STATUS.AVAILABLE, STATUS.SOLD, STATUS.LOCKED]
  const drilldownMap = useMemo(() => {
    if (!planDrilldown) return null
    return maps.find(m => m.name === planDrilldown.name) || null
  }, [planDrilldown, maps])

  const fetchMaps = async () => {
    try {
      const response = await fetch(config.getApiUrl('/maps'), {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setMaps(data)
        if (selectedMapForLots) {
          const updatedMap = data.find(map => map.id === selectedMapForLots.id)
          if (updatedMap) {
            setSelectedMapForLots(updatedMap)
          }
        }
        if (!statusColorMapId && data.length > 0) {
          setStatusColorMapId(data[0].id)
          setStatusColorInputs(prev => ({
            ...prev,
            ...((data[0].statusColors && typeof data[0].statusColors === 'object') ? data[0].statusColors : {})
          }))
        } else if (statusColorMapId) {
          const activeMap = data.find(map => map.id === statusColorMapId)
          if (activeMap) {
            setStatusColorInputs(prev => ({
              ...prev,
              ...((activeMap.statusColors && typeof activeMap.statusColors === 'object') ? activeMap.statusColors : {})
            }))
          }
        }
        if (!stageTemplateMapId && data.length > 0) {
          setStageTemplateMapId(data[0].id)
          setStageTemplateInput(data[0].stageTemplate || 'default')
        } else if (stageTemplateMapId) {
          const activeMap = data.find(map => map.id === stageTemplateMapId)
          if (activeMap) {
            setStageTemplateInput(activeMap.stageTemplate || 'default')
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch maps:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!statusColorMapId && maps.length > 0) {
      setStatusColorMapId(maps[0].id)
      setStatusColorInputs(prev => ({
        ...prev,
        ...((maps[0].statusColors && typeof maps[0].statusColors === 'object') ? maps[0].statusColors : {})
      }))
    }
  }, [maps, statusColorMapId])

  useEffect(() => {
    if (!stageTemplateMapId && maps.length > 0) {
      setStageTemplateMapId(maps[0].id)
      setStageTemplateInput(maps[0].stageTemplate || 'default')
    }
  }, [maps, stageTemplateMapId])

  useEffect(() => {
    if (!statusColorMapId) return
    const activeMap = maps.find(map => map.id === statusColorMapId)
    if (activeMap) {
      setStatusColorInputs({
        AVAILABLE: activeMap.statusColors?.AVAILABLE || statusColorDefaults.AVAILABLE,
        SOLD: activeMap.statusColors?.SOLD || statusColorDefaults.SOLD,
        LOCKED: activeMap.statusColors?.LOCKED || statusColorDefaults.LOCKED
      })
    }
  }, [statusColorMapId, maps, statusColorDefaults])

  const fetchEnquiries = async () => {
    try {
      const response = await fetch(config.getApiUrl('/enquiries'), {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setEnquiries(data)
      }
    } catch (err) {
      console.error('Failed to fetch enquiries:', err)
    }
  }

  // const syncEnquiryToCRM = async (enquiryId) => {
  //   setCrmSyncStatus(prev => ({ ...prev, [enquiryId]: 'syncing' }))
  //   try {
  //     const response = await fetch(`${config.getApiUrl('/enquiries')}/${enquiryId}/sync-crm`, {
  //       method: 'POST',
  //       headers: { 'Authorization': `Bearer ${token}` }
  //     })
  //     
  //     if (response.ok) {
  //       setCrmSyncStatus(prev => ({ ...prev, [enquiryId]: 'synced' }))
  //       setTimeout(() => {
  //         setCrmSyncStatus(prev => {
  //           const newStatus = { ...prev }
  //           delete newStatus[enquiryId]
  //           return newStatus
  //         })
  //       }, 3000)
  //     } else {
  //       setCrmSyncStatus(prev => ({ ...prev, [enquiryId]: 'error' }))
  //     }
  //   } catch (error) {
  //     console.error('Error syncing enquiry to CRM:', error)
  //     setCrmSyncStatus(prev => ({ ...prev, [enquiryId]: 'error' }))
  //   }
  // }

  const testCRMConnection = async () => {
    setCrmConnectionStatus('testing')
    try {
      const response = await fetch(`${config.getApiUrl('/enquiries')}/test-crm`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (response.ok) {
        setCrmConnectionStatus('connected')
        setTimeout(() => setCrmConnectionStatus(null), 3000)
      } else {
        setCrmConnectionStatus('error')
      }
    } catch (error) {
      console.error('Error testing CRM connection:', error)
      setCrmConnectionStatus('error')
    }
  }

  const fetchTenant = async () => {
    try {
      const response = await fetch(config.getApiUrl('/auth/tenant'), {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setTenant(data.tenant)
        setContactEmail(data.tenant?.contactEmail || '')
        setWebsite(data.tenant?.website || '')
      }
    } catch (err) {
      console.error('Failed to fetch tenant:', err)
    }
  }

  const handleSaveContactInfo = async () => {
    setSavingContact(true)
    try {
      const response = await fetch(config.getApiUrl('/auth/tenant/contact'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contactEmail: contactEmail.trim() || null,
          website: website.trim() || null
        })
      })
      if (response.ok) {
        const data = await response.json()
        setTenant(data.tenant)
        alert.success('Contact information saved successfully!')
      } else {
        alert.error('Failed to save contact information')
      }
    } catch (err) {
      console.error('Failed to save contact information:', err)
      alert.error('Failed to save contact information')
    } finally {
      setSavingContact(false)
    }
  }

  const handleStatusColorChange = (status, value) => {
    if (!/^#[0-9A-Fa-f]{0,6}$/.test(value)) return
    setStatusColorInputs(prev => ({ ...prev, [status]: value }))
  }

  const handleSaveStatusColors = async () => {
    if (!statusColorMapId) {
      alert.error('Please select a plan')
      return
    }
    const activeMap = maps.find(map => map.id === statusColorMapId)
    if (!activeMap) {
      alert.error('Selected plan not found')
      return
    }
    const hasEmpty = Object.values(statusColorInputs).some(color => !/^#[0-9A-Fa-f]{6}$/.test(color))
    if (hasEmpty) {
      alert.warning('Please enter valid hex colors (e.g., #FF0000)')
      return
    }
    setSavingStatusColors(true)
    try {
      const response = await fetch(config.getApiUrl(`/maps/${statusColorMapId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: activeMap.name,
          statusColors: JSON.stringify(statusColorInputs)
        })
      })
      if (!response.ok) {
        throw new Error('Failed to save colors')
      }
      try {
        await fetch(config.getApiUrl(`/regions/${statusColorMapId}/publish`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
      } catch (publishErr) {
        console.warn('Failed to publish after status color update', publishErr)
      }
      await fetchMaps()
      alert.success('Status colors updated')
    } catch (error) {
      console.error('Save status colors failed', error)
      alert.error('Failed to save status colors')
    } finally {
      setSavingStatusColors(false)
    }
  }

  const handleSaveStageTemplate = async () => {
    if (!stageTemplateMapId) {
      alert.error('Please select a plan')
      return
    }
    const activeMap = maps.find(map => map.id === stageTemplateMapId)
    if (!activeMap) {
      alert.error('Selected plan not found')
      return
    }
    if (stageTemplateInput !== 'default' && stageTemplateInput !== 'modern') {
      alert.error('Please select a valid template option')
      return
    }
    setSavingStageTemplate(true)
    try {
      const response = await fetch(config.getApiUrl(`/maps/${stageTemplateMapId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: activeMap.name,
          stageTemplate: stageTemplateInput
        })
      })
      if (!response.ok) {
        throw new Error('Failed to save stage template')
      }
      try {
        await fetch(config.getApiUrl(`/regions/${stageTemplateMapId}/publish`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
      } catch (publishErr) {
        console.warn('Failed to publish after stage template update', publishErr)
      }
      await fetchMaps()
      alert.success('Viewer theme updated successfully')
    } catch (error) {
      console.error('Save stage template failed', error)
      alert.error('Failed to save viewer theme')
    } finally {
      setSavingStageTemplate(false)
    }
  }

  const fetchChartData = async () => {
    if (!token) return
    
    setChartsLoading(true)
    try {
      const currentYear = new Date().getFullYear()
      const [lotStatusRes, enquiriesTimelineRes, planPerformanceRes, priceDistributionRes] = await Promise.all([
        fetch(config.getApiUrl('/charts/lot-status'), {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(config.getApiUrl(`/charts/enquiries-monthly?year=${currentYear}`), {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(config.getApiUrl('/charts/plan-performance'), {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(config.getApiUrl('/charts/price-distribution'), {
          headers: { Authorization: `Bearer ${token}` }
        })
      ])

      const [lotStatus, enquiriesTimeline, planPerformance, priceDistribution] = await Promise.all([
        lotStatusRes.ok ? lotStatusRes.json() : [],
        enquiriesTimelineRes.ok ? enquiriesTimelineRes.json() : { labels: [], values: [], datasetLabel: 'Enquiries' },
        planPerformanceRes.ok ? planPerformanceRes.json() : { labels: [], datasets: [] },
        priceDistributionRes.ok ? priceDistributionRes.json() : []
      ])

      setChartData({
        lotStatus,
        enquiriesTimeline,
        planPerformance,
        priceDistribution
      })
    } catch (err) {
      console.error('Failed to fetch chart data:', err)
    } finally {
      setChartsLoading(false)
    }
  }

  useEffect(() => {
    if (token) {
      fetchMaps()
      fetchEnquiries()
      fetchTenant()
      fetchChartData()
    }
  }, [token])

  // Reset and auto-select first stage when map changes
  useEffect(() => {
    if (selectedMapForLots && !onlyLivePlans) {
      if (!selectedMapForLots.regions) {
        if (selectedStage !== null) setSelectedStage(null)
        if (selectedGroupFilter) setSelectedGroupFilter(null)
        return
      }
      
      const stageMap = new Map()
      selectedMapForLots.regions.forEach(region => {
        const isStaged = region.name && String(region.name).trim() !== ''
        if (!isStaged) return
        const stageName = region.name
        if (!stageMap.has(stageName)) {
          stageMap.set(stageName, { name: stageName })
        }
      })
      
      const stages = Array.from(stageMap.values()).sort((a, b) => a.name.localeCompare(b.name))
      if (stages.length === 0) {
        if (selectedStage !== null) setSelectedStage(null)
        if (selectedGroupFilter) setSelectedGroupFilter(null)
        return
      }
      const stageNames = stages.map(stage => stage.name)
      if (!selectedStage || !stageNames.includes(selectedStage)) {
        setSelectedStage(stages[0].name)
        if (selectedGroupFilter) setSelectedGroupFilter(null)
      } else if (selectedGroupFilter && selectedGroupFilter.stageName !== selectedStage) {
        setSelectedGroupFilter(null)
      }
    } else {
      if (selectedStage !== null) setSelectedStage(null)
      if (selectedGroupFilter) setSelectedGroupFilter(null)
    }
  }, [selectedMapForLots, onlyLivePlans, selectedStage, selectedGroupFilter])

  useEffect(() => {
    if (quickEdit && quickEdit.stageName !== selectedStage) {
      setQuickEdit(null)
      setCreatingGroup(false)
      setCreatingSubGroup(null)
      setRenamingGroup(null)
      setRenamingSubGroup(null)
      setEditingGroupColor(null)
    }
  }, [selectedStage])

  useEffect(() => {
    if (onlyLivePlans && quickEdit) {
      setQuickEdit(null)
      setCreatingGroup(false)
      setCreatingSubGroup(null)
      setRenamingGroup(null)
      setRenamingSubGroup(null)
      setEditingGroupColor(null)
    }
  }, [onlyLivePlans])

  // Set document title based on user role
  useEffect(() => {
    if (user?.role === 'SUPER_ADMIN') {
      document.title = 'Super Admin'
    } else if (user?.role === 'ADMIN') {
      document.title = 'Admin'
    } else {
      document.title = 'Admin'
    }
    return () => {
      document.title = 'React App'
    }
  }, [user?.role])

  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth <= 992)
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const handleMapSelect = (map) => {
    setSelectedMap(map)
    setCurrentView('editor')
  }

  const handleMapCreated = () => {
    fetchMaps()
    setCurrentView('list')
  }

  const handleBackToList = () => {
    setCurrentView('list')
    setSelectedMap(null)
  }

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/gif']
    if (!allowedTypes.includes(file.type)) {
      alert.error('Please select a valid image file (JPEG, PNG, SVG, or GIF)')
      return
    }

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      alert.error('File size must be less than 2MB')
      return
    }

    setLogoUploading(true)
    try {
      const formData = new FormData()
      formData.append('logo', file)

      const response = await fetch(config.getApiUrl('/auth/tenant/logo'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      })

      if (response.ok) {
        const data = await response.json()
        setTenant(prev => ({ ...prev, logoUrl: data.logoUrl }))
        alert.success('Logo uploaded successfully!')
      } else {
        const errorData = await response.json()
        alert.error(`Failed to upload logo: ${errorData.error}`)
      }
    } catch (err) {
      console.error('Logo upload error:', err)
      alert.error('Failed to upload logo. Please try again.')
    } finally {
      setLogoUploading(false)
      // Reset file input
      event.target.value = ''
    }
  }

  const openQuickEdit = (region) => {
    if (!region || !region.id) return
    const stageName = (region.name && String(region.name).trim()) || 'Ungrouped'
    setQuickEdit({
      regionId: region.id,
      stageName,
      status: region.status || STATUS.AVAILABLE,
      group: region.customProps?.group || '',
      subGroup: region.customProps?.subGroup || '',
      lockReason: region.customProps?.lockReason || ''
    })
  }

  const closeQuickEdit = () => {
    setQuickEdit(null)
    setCreatingGroup(false)
    setCreatingSubGroup(null)
    setRenamingGroup(null)
    setRenamingSubGroup(null)
    setEditingGroupColor(null)
    setNewGroupName('')
    setNewSubGroupName('')
  }

  const updateQuickEditField = (field, value) => {
    setQuickEdit(prev => {
      if (!prev) return prev
      if (field === 'group') {
        return { ...prev, group: value, subGroup: '' }
      }
      if (field === 'status' && value !== STATUS.LOCKED) {
        // Clear lockReason when status changes from LOCKED
        return { ...prev, status: value, lockReason: '' }
      }
      return { ...prev, [field]: value }
    })
  }

  const handleQuickEditSave = async () => {
    if (!quickEdit || !selectedMapForLots) return
    const targetMapId = selectedMapForLots.id
    try {
      setQuickEditSaving(true)
      const groupValue = quickEdit.group ? quickEdit.group : null
      const subGroupValue = groupValue ? (quickEdit.subGroup || null) : null
      // Only set lockReason if status is LOCKED, otherwise set to null to remove it
      const lockReasonValue = quickEdit.status === STATUS.LOCKED && quickEdit.lockReason ? quickEdit.lockReason.trim() : null

      const response = await fetch(config.getApiUrl('/regions/bulk'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          updates: [{
            id: quickEdit.regionId,
            status: quickEdit.status,
            group: groupValue,
            subGroup: subGroupValue,
            lockReason: lockReasonValue
          }]
        })
      })

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}))
        throw new Error(errBody.error || 'Failed to update lot')
      }

      const applyLocalUpdate = (regions = []) => regions.map(region => {
        if (region.id !== quickEdit.regionId) return region
        const nextCustomProps = { ...(region.customProps || {}) }
        if (groupValue) {
          nextCustomProps.group = groupValue
        } else {
          delete nextCustomProps.group
        }
        if (subGroupValue) {
          nextCustomProps.subGroup = subGroupValue
        } else {
          delete nextCustomProps.subGroup
        }
        if (!groupValue) {
          delete nextCustomProps.subGroup
        }
        // Handle lockReason
        if (quickEdit.status === STATUS.LOCKED && lockReasonValue) {
          nextCustomProps.lockReason = lockReasonValue
        } else {
          delete nextCustomProps.lockReason
        }
        const cleaned = Object.keys(nextCustomProps).length > 0 ? nextCustomProps : null
        return { ...region, status: quickEdit.status, customProps: cleaned }
      })

      setSelectedMapForLots(prev => {
        if (!prev) return prev
        return { ...prev, regions: applyLocalUpdate(prev.regions || []) }
      })

      setMaps(prev => prev.map(map => {
        if (map.id !== targetMapId) return map
        if (!map.regions) return map
        return { ...map, regions: applyLocalUpdate(map.regions) }
      }))

      // Remove from unassigned sets if group/sub-group was assigned
      if (groupValue && unassignedGroups.has(groupValue)) {
        setUnassignedGroups(prev => {
          const newSet = new Set(prev)
          newSet.delete(groupValue)
          return newSet
        })
      }
      if (subGroupValue && groupValue) {
        const unassignedSubs = unassignedSubGroups.get(groupValue)
        if (unassignedSubs && unassignedSubs.has(subGroupValue)) {
          setUnassignedSubGroups(prev => {
            const newMap = new Map(prev)
            const subGroupSet = newMap.get(groupValue)
            if (subGroupSet) {
              subGroupSet.delete(subGroupValue)
              if (subGroupSet.size === 0) {
                newMap.delete(groupValue)
              } else {
                newMap.set(groupValue, subGroupSet)
              }
            }
            return newMap
          })
        }
      }

      // Publish the map after updating
      try {
        const publishResponse = await fetch(config.getApiUrl(`/regions/${targetMapId}/publish`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!publishResponse.ok) {
          console.warn('Failed to publish map after update')
          // Don't throw - the update was successful, publishing is secondary
        } else {
          // Refresh maps to get updated version info
          await fetchMaps()
        }
      } catch (publishError) {
        console.error('Publish error:', publishError)
        // Don't throw - the update was successful, publishing is secondary
      }

      alert.success('Lot updated and published successfully')
      setQuickEdit(null)
    } catch (error) {
      console.error('Quick edit update failed:', error)
      alert.error(error.message || 'Failed to update lot')
    } finally {
      setQuickEditSaving(false)
    }
  }

  // Group color helpers
  const groupColorPalette = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6', '#0ea5e9', '#22c55e', '#eab308', '#f97316', '#14b8a6', '#a855f7', '#f43f5e']
  
  const getGroupColor = (groupName, region = null, subGroupName = null) => {
    if (!groupName) return '#6b7280'
    
    // Handle combined format "group::subGroup" for backward compatibility
    let actualGroupName = groupName
    let actualSubGroupName = subGroupName
    if (typeof groupName === 'string' && groupName.includes('::') && !subGroupName) {
      const parts = groupName.split('::')
      actualGroupName = parts[0]
      actualSubGroupName = parts[1] || null
    }
    
    // First, try to get color from region's customProps if available (database)
    if (region) {
      if (actualSubGroupName && region.customProps?.subGroup === actualSubGroupName && region.customProps?.subGroupColor) {
        return region.customProps.subGroupColor
      }
      if (!actualSubGroupName && region.customProps?.group === actualGroupName && region.customProps?.groupColor) {
        return region.customProps.groupColor
      }
    }
    
    // Try to find color in any region with this group/sub-group (from database)
    if (selectedMapForLots && selectedMapForLots.regions) {
      const matchingRegion = selectedMapForLots.regions.find(r => {
        if (actualSubGroupName) {
          return r.customProps?.group === actualGroupName && r.customProps?.subGroup === actualSubGroupName
        } else {
          return r.customProps?.group === actualGroupName
        }
      })
      
      if (matchingRegion) {
        if (actualSubGroupName && matchingRegion.customProps?.subGroupColor) {
          return matchingRegion.customProps.subGroupColor
        }
        if (!actualSubGroupName && matchingRegion.customProps?.groupColor) {
          return matchingRegion.customProps.groupColor
        }
      }
    }
    
    // Fallback to localStorage (for backward compatibility)
    const key = actualSubGroupName ? `${actualGroupName}::${actualSubGroupName}` : actualGroupName
    const stored = localStorage.getItem(`group_color_${key}`)
    if (stored) return stored
    
    // Generate color from hash
    let hash = 0
    const nameToHash = actualSubGroupName ? `${actualGroupName}::${actualSubGroupName}` : actualGroupName
    for (let i = 0; i < nameToHash.length; i++) {
      hash = ((hash << 5) - hash) + nameToHash.charCodeAt(i)
    }
    const colorIndex = Math.abs(hash) % groupColorPalette.length
    return groupColorPalette[colorIndex]
  }
  
  const setGroupColor = (groupName, color) => {
    if (groupName && color) {
      localStorage.setItem(`group_color_${groupName}`, color)
      setColorUpdateTrigger(prev => prev + 1)
    }
  }

  const handleCreateGroup = () => {
    const trimmedName = newGroupName.trim()
    if (!trimmedName) {
      alert.warning('Please enter a group name')
      return
    }
    
    if (!selectedMapForLots || !selectedMapForLots.regions) return
    
    // Check if group already exists in regions or unassigned groups
    const existingGroups = new Set()
    selectedMapForLots.regions.forEach(region => {
      if (region.customProps?.group) {
        existingGroups.add(region.customProps.group)
      }
    })
    
    if (existingGroups.has(trimmedName) || unassignedGroups.has(trimmedName)) {
      alert.warning('A group with this name already exists')
      return
    }
    
    // Add to unassigned groups
    setUnassignedGroups(prev => new Set([...prev, trimmedName]))
    
    // Select the new group in quick edit
    if (quickEdit) {
      setQuickEdit({ ...quickEdit, group: trimmedName, subGroup: '' })
    }
    setNewGroupName('')
    setCreatingGroup(false)
    alert.success(`Group "${trimmedName}" created. Assign it to a lot to save.`)
  }

  const handleCreateSubGroup = (groupName) => {
    const trimmedName = newSubGroupName.trim()
    if (!trimmedName) {
      alert.warning('Please enter a sub-group name')
      return
    }
    
    if (!selectedMapForLots || !selectedMapForLots.regions) return
    
    // Check if sub-group already exists in this group (in regions or unassigned)
    const existingSubGroups = new Set()
    selectedMapForLots.regions.forEach(region => {
      if (region.customProps?.group === groupName && region.customProps?.subGroup) {
        existingSubGroups.add(region.customProps.subGroup)
      }
    })
    
    const unassignedSubs = unassignedSubGroups.get(groupName)
    if (existingSubGroups.has(trimmedName) || (unassignedSubs && unassignedSubs.has(trimmedName))) {
      alert.warning('A sub-group with this name already exists in this group')
      return
    }
    
    // Add to unassigned sub-groups
    setUnassignedSubGroups(prev => {
      const newMap = new Map(prev)
      const subGroupSet = newMap.get(groupName) || new Set()
      subGroupSet.add(trimmedName)
      newMap.set(groupName, subGroupSet)
      return newMap
    })
    
    // Select the new sub-group in quick edit
    if (quickEdit && quickEdit.group === groupName) {
      setQuickEdit({ ...quickEdit, subGroup: trimmedName })
    }
    setNewSubGroupName('')
    setCreatingSubGroup(null)
    alert.success(`Sub-group "${trimmedName}" created. Assign it to a lot to save.`)
  }

  const handleRenameGroup = async (oldName, newName) => {
    const trimmedNewName = newName.trim()
    if (!trimmedNewName || trimmedNewName === oldName) {
      setRenamingGroup(null)
      return
    }
    
    if (!selectedMapForLots || !selectedMapForLots.regions) return
    
    // Check if new name already exists (in regions or unassigned groups)
    const existingGroups = new Set()
    selectedMapForLots.regions.forEach(region => {
      if (region.customProps?.group && region.customProps.group !== oldName) {
        existingGroups.add(region.customProps.group)
      }
    })
    
    if (existingGroups.has(trimmedNewName) || (unassignedGroups.has(trimmedNewName) && trimmedNewName !== oldName)) {
      alert.warning('A group with this name already exists')
      setRenamingGroup(null)
      return
    }
    
    // Find all regions with this group
    const regionsToUpdate = selectedMapForLots.regions.filter(r => r.customProps?.group === oldName)
    const isUnassigned = unassignedGroups.has(oldName)
    
    if (regionsToUpdate.length === 0 && !isUnassigned) {
      setRenamingGroup(null)
      return
    }
    
    // Migrate color
    const oldColor = localStorage.getItem(`group_color_${oldName}`)
    if (oldColor) {
      localStorage.setItem(`group_color_${trimmedNewName}`, oldColor)
      localStorage.removeItem(`group_color_${oldName}`)
    }
    
    // Migrate sub-group colors
    const subGroups = new Set()
    regionsToUpdate.forEach(region => {
      if (region.customProps?.subGroup) {
        subGroups.add(region.customProps.subGroup)
      }
    })
    subGroups.forEach(subGroupName => {
      const oldSubColorKey = `${oldName}::${subGroupName}`
      const newSubColorKey = `${trimmedNewName}::${subGroupName}`
      const oldSubColor = localStorage.getItem(`group_color_${oldSubColorKey}`)
      if (oldSubColor) {
        localStorage.setItem(`group_color_${newSubColorKey}`, oldSubColor)
        localStorage.removeItem(`group_color_${oldSubColorKey}`)
      }
    })
    
    try {
      // Update all regions with this group
      const updates = regionsToUpdate.map(region => ({
        id: region.id,
        group: trimmedNewName,
        subGroup: region.customProps?.subGroup || null
      }))
      
      const response = await fetch(config.getApiUrl('/regions/bulk'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ updates })
      })
      
      if (!response.ok) {
        throw new Error('Failed to rename group')
      }
      
      // Update local state
      const applyRename = (regions = []) => regions.map(region => {
        if (region.customProps?.group === oldName) {
          const newCustomProps = { ...region.customProps, group: trimmedNewName }
          return { ...region, customProps: newCustomProps }
        }
        return region
      })
      
      setSelectedMapForLots(prev => {
        if (!prev) return prev
        return { ...prev, regions: applyRename(prev.regions || []) }
      })
      
      setMaps(prev => prev.map(map => {
        if (map.id !== selectedMapForLots.id) return map
        if (!map.regions) return map
        return { ...map, regions: applyRename(map.regions) }
      }))
      
      // Update unassigned groups if needed
      if (isUnassigned) {
        setUnassignedGroups(prev => {
          const newSet = new Set(prev)
          newSet.delete(oldName)
          newSet.add(trimmedNewName)
          return newSet
        })
      }
      
      // Update unassigned sub-groups map key if needed
      const oldUnassignedSubs = unassignedSubGroups.get(oldName)
      if (oldUnassignedSubs && oldUnassignedSubs.size > 0) {
        setUnassignedSubGroups(prev => {
          const newMap = new Map(prev)
          newMap.delete(oldName)
          newMap.set(trimmedNewName, oldUnassignedSubs)
          return newMap
        })
      }
      
      // Update quick edit if it references this group
      if (quickEdit && quickEdit.group === oldName) {
        setQuickEdit({ ...quickEdit, group: trimmedNewName })
      }
      
      // Publish the map after renaming
      try {
        const publishResponse = await fetch(config.getApiUrl(`/regions/${selectedMapForLots.id}/publish`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!publishResponse.ok) {
          console.warn('Failed to publish map after rename')
        } else {
          await fetchMaps()
        }
      } catch (publishError) {
        console.error('Publish error:', publishError)
      }
      
      alert.success(`Group renamed from "${oldName}" to "${trimmedNewName}" and published`)
      setRenamingGroup(null)
      setColorUpdateTrigger(prev => prev + 1)
    } catch (error) {
      console.error('Rename group failed:', error)
      alert.error('Failed to rename group')
      setRenamingGroup(null)
    }
  }

  const handleRenameSubGroup = async (groupName, oldName, newName) => {
    const trimmedNewName = newName.trim()
    if (!trimmedNewName || trimmedNewName === oldName) {
      setRenamingSubGroup(null)
      return
    }
    
    if (!selectedMapForLots || !selectedMapForLots.regions) return
    
    // Check if new name already exists in this group (in regions or unassigned)
    const existingSubGroups = new Set()
    selectedMapForLots.regions.forEach(region => {
      if (region.customProps?.group === groupName && region.customProps?.subGroup && region.customProps.subGroup !== oldName) {
        existingSubGroups.add(region.customProps.subGroup)
      }
    })
    
    const unassignedSubs = unassignedSubGroups.get(groupName)
    if (existingSubGroups.has(trimmedNewName) || (unassignedSubs && unassignedSubs.has(trimmedNewName) && trimmedNewName !== oldName)) {
      alert.warning('A sub-group with this name already exists in this group')
      setRenamingSubGroup(null)
      return
    }
    
    // Find all regions with this group and sub-group
    const regionsToUpdate = selectedMapForLots.regions.filter(r => 
      r.customProps?.group === groupName && r.customProps?.subGroup === oldName
    )
    
    const isUnassigned = unassignedSubs && unassignedSubs.has(oldName)
    
    if (regionsToUpdate.length === 0 && !isUnassigned) {
      setRenamingSubGroup(null)
      return
    }
    
    // Migrate color
    const oldColorKey = `${groupName}::${oldName}`
    const newColorKey = `${groupName}::${trimmedNewName}`
    const oldColor = localStorage.getItem(`group_color_${oldColorKey}`)
    if (oldColor) {
      localStorage.setItem(`group_color_${newColorKey}`, oldColor)
      localStorage.removeItem(`group_color_${oldColorKey}`)
    }
    
    try {
      // Update all regions with this sub-group
      const updates = regionsToUpdate.map(region => ({
        id: region.id,
        group: groupName,
        subGroup: trimmedNewName
      }))
      
      const response = await fetch(config.getApiUrl('/regions/bulk'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ updates })
      })
      
      if (!response.ok) {
        throw new Error('Failed to rename sub-group')
      }
      
      // Update local state
      const applyRename = (regions = []) => regions.map(region => {
        if (region.customProps?.group === groupName && region.customProps?.subGroup === oldName) {
          const newCustomProps = { ...region.customProps, subGroup: trimmedNewName }
          return { ...region, customProps: newCustomProps }
        }
        return region
      })
      
      setSelectedMapForLots(prev => {
        if (!prev) return prev
        return { ...prev, regions: applyRename(prev.regions || []) }
      })
      
      setMaps(prev => prev.map(map => {
        if (map.id !== selectedMapForLots.id) return map
        if (!map.regions) return map
        return { ...map, regions: applyRename(map.regions) }
      }))
      
      // Update unassigned sub-groups if needed
      if (isUnassigned) {
        setUnassignedSubGroups(prev => {
          const newMap = new Map(prev)
          const subGroupSet = newMap.get(groupName)
          if (subGroupSet) {
            subGroupSet.delete(oldName)
            subGroupSet.add(trimmedNewName)
            newMap.set(groupName, subGroupSet)
          }
          return newMap
        })
      }
      
      // Update quick edit if it references this sub-group
      if (quickEdit && quickEdit.group === groupName && quickEdit.subGroup === oldName) {
        setQuickEdit({ ...quickEdit, subGroup: trimmedNewName })
      }
      
      // Publish the map after renaming
      try {
        const publishResponse = await fetch(config.getApiUrl(`/regions/${selectedMapForLots.id}/publish`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!publishResponse.ok) {
          console.warn('Failed to publish map after rename')
        } else {
          await fetchMaps()
        }
      } catch (publishError) {
        console.error('Publish error:', publishError)
      }
      
      alert.success(`Sub-group renamed from "${oldName}" to "${trimmedNewName}" and published`)
      setRenamingSubGroup(null)
      setColorUpdateTrigger(prev => prev + 1)
    } catch (error) {
      console.error('Rename sub-group failed:', error)
      alert.error('Failed to rename sub-group')
      setRenamingSubGroup(null)
    }
  }

  const handleChangeGroupColor = async (groupName, color, subGroupName = null) => {
    if (!color || !color.match(/^#[0-9A-Fa-f]{6}$/)) {
      alert.warning('Please enter a valid hex color (e.g., #FF0000)')
      return
    }
    if (!selectedMapForLots || !selectedMapForLots.regions) {
      alert.error('No map selected')
      return
    }

    const key = subGroupName ? `${groupName}::${subGroupName}` : groupName
    // Save to localStorage for backward compatibility
    setGroupColor(key, color)
    
    // Find all regions that belong to this group/sub-group
    const regionsToUpdate = selectedMapForLots.regions.filter(region => {
      if (subGroupName) {
        // For sub-groups, match both group and sub-group
        return region.customProps?.group === groupName && region.customProps?.subGroup === subGroupName
      } else {
        // For groups, match group (regardless of sub-group)
        return region.customProps?.group === groupName
      }
    })

    if (regionsToUpdate.length > 0) {
      try {
        // Update all regions with the color in customProps
        const updates = regionsToUpdate.map(region => {
          const customProps = { ...(region.customProps || {}) }
          if (subGroupName) {
            // Store sub-group color
            customProps.subGroupColor = color
          } else {
            // Store group color
            customProps.groupColor = color
          }
          return {
            id: region.id,
            customProps: customProps
          }
        })

        const response = await fetch(config.getApiUrl('/regions/bulk'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ updates })
        })

        if (!response.ok) {
          throw new Error('Failed to update group color in database')
        }

        // Update local state
        setSelectedMapForLots(prev => {
          if (!prev) return prev
          return {
            ...prev,
            regions: prev.regions.map(region => {
              const shouldUpdate = subGroupName
                ? region.customProps?.group === groupName && region.customProps?.subGroup === subGroupName
                : region.customProps?.group === groupName
              
              if (shouldUpdate) {
                const customProps = { ...(region.customProps || {}) }
                if (subGroupName) {
                  customProps.subGroupColor = color
                } else {
                  customProps.groupColor = color
                }
                return { ...region, customProps }
              }
              return region
            })
          }
        })

        // Update maps state
        setMaps(prev => prev.map(map => {
          if (map.id !== selectedMapForLots.id) return map
          if (!map.regions) return map
          return {
            ...map,
            regions: map.regions.map(region => {
              const shouldUpdate = subGroupName
                ? region.customProps?.group === groupName && region.customProps?.subGroup === subGroupName
                : region.customProps?.group === groupName
              
              if (shouldUpdate) {
                const customProps = { ...(region.customProps || {}) }
                if (subGroupName) {
                  customProps.subGroupColor = color
                } else {
                  customProps.groupColor = color
                }
                return { ...region, customProps }
              }
              return region
            })
          }
        }))

        // Publish the map after updating
        try {
          const publishResponse = await fetch(config.getApiUrl(`/regions/${selectedMapForLots.id}/publish`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            }
          })

          if (!publishResponse.ok) {
            console.warn('Failed to publish map after color update')
          } else {
            await fetchMaps()
          }
        } catch (publishError) {
          console.error('Publish error:', publishError)
        }

        setEditingGroupColor(null)
        setColorUpdateTrigger(prev => prev + 1)
        const displayName = subGroupName ? `${groupName} > ${subGroupName}` : groupName
        alert.success(`Color updated for ${displayName} and synced across all devices`)
      } catch (error) {
        console.error('Failed to update group color:', error)
        alert.error('Failed to save color to database. It was saved locally only.')
      }
    } else {
      // No regions found, but still save to localStorage for future use
      setEditingGroupColor(null)
      setColorUpdateTrigger(prev => prev + 1)
      const displayName = subGroupName ? `${groupName} > ${subGroupName}` : groupName
      alert.success(`Color saved for ${displayName} (will apply when lots are assigned)`)
    }
  }

  const handleDeleteGroup = async (groupName) => {
    if (!groupName || !selectedMapForLots || !selectedMapForLots.regions) return
    
    if (!window.confirm(`Are you sure you want to delete the group "${groupName}"? This will remove it from all lots that have it assigned.`)) {
      return
    }
    
    // Find all regions with this group
    const regionsToUpdate = selectedMapForLots.regions.filter(r => r.customProps?.group === groupName)
    
    if (regionsToUpdate.length === 0 && !unassignedGroups.has(groupName)) {
      alert.warning('Group not found')
      return
    }
    
    try {
      // Remove group from all regions
      if (regionsToUpdate.length > 0) {
        const updates = regionsToUpdate.map(region => ({
          id: region.id,
          group: null,
          subGroup: null // Also remove sub-group when group is removed
        }))
        
        const response = await fetch(config.getApiUrl('/regions/bulk'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ updates })
        })
        
        if (!response.ok) {
          throw new Error('Failed to delete group')
        }
        
        // Update local state
        const applyDelete = (regions = []) => regions.map(region => {
          if (region.customProps?.group === groupName) {
            const newCustomProps = { ...region.customProps }
            delete newCustomProps.group
            delete newCustomProps.subGroup
            const cleaned = Object.keys(newCustomProps).length > 0 ? newCustomProps : null
            return { ...region, customProps: cleaned }
          }
          return region
        })
        
        setSelectedMapForLots(prev => {
          if (!prev) return prev
          return { ...prev, regions: applyDelete(prev.regions || []) }
        })
        
        setMaps(prev => prev.map(map => {
          if (map.id !== selectedMapForLots.id) return map
          if (!map.regions) return map
          return { ...map, regions: applyDelete(map.regions) }
        }))
      }
      
      // Remove from unassigned groups
      if (unassignedGroups.has(groupName)) {
        setUnassignedGroups(prev => {
          const newSet = new Set(prev)
          newSet.delete(groupName)
          return newSet
        })
      }
      
      // Remove unassigned sub-groups for this group
      if (unassignedSubGroups.has(groupName)) {
        setUnassignedSubGroups(prev => {
          const newMap = new Map(prev)
          newMap.delete(groupName)
          return newMap
        })
      }
      
      // Remove group color from localStorage
      localStorage.removeItem(`group_color_${groupName}`)
      
      // Remove all sub-group colors for this group
      const allKeys = Object.keys(localStorage)
      allKeys.forEach(key => {
        if (key.startsWith(`group_color_${groupName}::`)) {
          localStorage.removeItem(key)
        }
      })
      
      // Clear from quick edit if it references this group
      if (quickEdit && quickEdit.group === groupName) {
        setQuickEdit({ ...quickEdit, group: '', subGroup: '' })
      }
      
      // Publish the map after deletion
      try {
        const publishResponse = await fetch(config.getApiUrl(`/regions/${selectedMapForLots.id}/publish`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!publishResponse.ok) {
          console.warn('Failed to publish map after delete')
        } else {
          await fetchMaps()
        }
      } catch (publishError) {
        console.error('Publish error:', publishError)
      }
      
      alert.success(`Group "${groupName}" deleted and published`)
      setColorUpdateTrigger(prev => prev + 1)
    } catch (error) {
      console.error('Delete group failed:', error)
      alert.error('Failed to delete group')
    }
  }

  const handleDeleteSubGroup = async (groupName, subGroupName) => {
    if (!groupName || !subGroupName || !selectedMapForLots || !selectedMapForLots.regions) return
    
    if (!window.confirm(`Are you sure you want to delete the sub-group "${subGroupName}" from group "${groupName}"? This will remove it from all lots that have it assigned.`)) {
      return
    }
    
    // Find all regions with this group and sub-group
    const regionsToUpdate = selectedMapForLots.regions.filter(r => 
      r.customProps?.group === groupName && r.customProps?.subGroup === subGroupName
    )
    
    const unassignedSubs = unassignedSubGroups.get(groupName)
    const isUnassigned = unassignedSubs && unassignedSubs.has(subGroupName)
    
    if (regionsToUpdate.length === 0 && !isUnassigned) {
      alert.warning('Sub-group not found')
      return
    }
    
    try {
      // Remove sub-group from all regions
      if (regionsToUpdate.length > 0) {
        const updates = regionsToUpdate.map(region => ({
          id: region.id,
          group: groupName,
          subGroup: null
        }))
        
        const response = await fetch(config.getApiUrl('/regions/bulk'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ updates })
        })
        
        if (!response.ok) {
          throw new Error('Failed to delete sub-group')
        }
        
        // Update local state
        const applyDelete = (regions = []) => regions.map(region => {
          if (region.customProps?.group === groupName && region.customProps?.subGroup === subGroupName) {
            const newCustomProps = { ...region.customProps }
            delete newCustomProps.subGroup
            const cleaned = Object.keys(newCustomProps).length > 0 ? newCustomProps : null
            return { ...region, customProps: cleaned }
          }
          return region
        })
        
        setSelectedMapForLots(prev => {
          if (!prev) return prev
          return { ...prev, regions: applyDelete(prev.regions || []) }
        })
        
        setMaps(prev => prev.map(map => {
          if (map.id !== selectedMapForLots.id) return map
          if (!map.regions) return map
          return { ...map, regions: applyDelete(map.regions) }
        }))
      }
      
      // Remove from unassigned sub-groups
      if (isUnassigned) {
        setUnassignedSubGroups(prev => {
          const newMap = new Map(prev)
          const subGroupSet = newMap.get(groupName)
          if (subGroupSet) {
            subGroupSet.delete(subGroupName)
            if (subGroupSet.size === 0) {
              newMap.delete(groupName)
            } else {
              newMap.set(groupName, subGroupSet)
            }
          }
          return newMap
        })
      }
      
      // Remove sub-group color from localStorage
      const colorKey = `group_color_${groupName}::${subGroupName}`
      localStorage.removeItem(colorKey)
      
      // Clear from quick edit if it references this sub-group
      if (quickEdit && quickEdit.group === groupName && quickEdit.subGroup === subGroupName) {
        setQuickEdit({ ...quickEdit, subGroup: '' })
      }
      
      // Publish the map after deletion
      try {
        const publishResponse = await fetch(config.getApiUrl(`/regions/${selectedMapForLots.id}/publish`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!publishResponse.ok) {
          console.warn('Failed to publish map after delete')
        } else {
          await fetchMaps()
        }
      } catch (publishError) {
        console.error('Publish error:', publishError)
      }
      
      alert.success(`Sub-group "${subGroupName}" deleted and published`)
      setColorUpdateTrigger(prev => prev + 1)
    } catch (error) {
      console.error('Delete sub-group failed:', error)
      alert.error('Failed to delete sub-group')
    }
  }

  const exportLivePlans = () => {
    const livePlans = maps.filter(m => m.versions && m.versions.length > 0)
    const columns = [
      { key: 'name', label: 'Plan Name' },
      { key: 'createdAt', label: 'Created Date', formatter: formatDate },
      { key: 'totalLots', label: 'Total Lots', formatter: (value, item) => item.regions?.filter(r => r.name && String(r.name).trim() !== '').length || 0 },
      { key: 'availableLots', label: 'Available Lots', formatter: (value, item) => item.regions?.filter(r => r.name && String(r.name).trim() !== '' && r.status === 'AVAILABLE').length || 0 },
      { key: 'soldLots', label: 'Sold Lots', formatter: (value, item) => item.regions?.filter(r => r.name && String(r.name).trim() !== '' && r.status === 'SOLD').length || 0 },
      { key: 'lockedLots', label: 'Locked Lots', formatter: (value, item) => item.regions?.filter(r => r.name && String(r.name).trim() !== '' && r.status === 'LOCKED').length || 0 }
    ]
    
    const csvContent = arrayToCSV(livePlans, columns)
    const filename = `live-plans-${new Date().toISOString().split('T')[0]}.csv`
    downloadCSV(csvContent, filename)
  }

  const exportLotsInSystem = () => {
    const allMaps = maps
    const columns = [
      { key: 'name', label: 'Plan Name' },
      { key: 'createdAt', label: 'Created Date', formatter: formatDate },
      { key: 'totalLots', label: 'Total Lots', formatter: (value, item) => item.regions?.filter(r => r.name && String(r.name).trim() !== '').length || 0 },
      { key: 'availableLots', label: 'Available Lots', formatter: (value, item) => item.regions?.filter(r => r.name && String(r.name).trim() !== '' && r.status === 'AVAILABLE').length || 0 },
      { key: 'soldLots', label: 'Sold Lots', formatter: (value, item) => item.regions?.filter(r => r.name && String(r.name).trim() !== '' && r.status === 'SOLD').length || 0 },
      { key: 'lockedLots', label: 'Locked Lots', formatter: (value, item) => item.regions?.filter(r => r.name && String(r.name).trim() !== '' && r.status === 'LOCKED').length || 0 },
      { key: 'isLive', label: 'Is Live Plan', formatter: (value, item) => item.versions && item.versions.length > 0 ? 'Yes' : 'No' }
    ]
    
    const csvContent = arrayToCSV(allMaps, columns)
    const filename = `lots-in-system-${new Date().toISOString().split('T')[0]}.csv`
    downloadCSV(csvContent, filename)
  }

  // Get all sales groups from the selected map (from lot customProps) with counts and sub-groups
  const getAvailableGroups = () => {
    if (!selectedMapForLots || !selectedMapForLots.regions) return []
    const groupMap = new Map() // Map<`${stageName}::${groupName}`, { stageName, groupName, count, subGroups: Map<subGroupName, count> }>
    
    selectedMapForLots.regions.forEach(region => {
      const isStaged = region.name && String(region.name).trim() !== ''
      if (!isStaged) return
      
      // Apply status filter to only show groups for relevant lots
      let statusMatch = true
      if (onlyLivePlans) {
        statusMatch = region.status === 'AVAILABLE'
      } else if (activeLotTab === 'available') {
        statusMatch = region.status === 'AVAILABLE'
      } else if (activeLotTab === 'sold') {
        statusMatch = region.status === 'SOLD'
      } else if (activeLotTab === 'locked') {
        statusMatch = region.status === 'LOCKED'
      }
      // For 'all' tab, statusMatch remains true
      
      if (statusMatch && region.customProps?.group && region.name) {
        const groupKey = `${region.name}::${region.customProps.group}`
        if (!groupMap.has(groupKey)) {
          groupMap.set(groupKey, {
            stageName: region.name,
            groupName: region.customProps.group,
            count: 0,
            subGroups: new Map() // Map<subGroupName, count>
          })
        }
        const group = groupMap.get(groupKey)
        group.count++
        
        // Track sub-groups
        if (region.customProps?.subGroup) {
          const subGroupName = region.customProps.subGroup
          if (!group.subGroups.has(subGroupName)) {
            group.subGroups.set(subGroupName, 0)
          }
          group.subGroups.set(subGroupName, group.subGroups.get(subGroupName) + 1)
        }
      }
    })
    
    // Convert subGroups Maps to arrays
    return Array.from(groupMap.values()).map(group => ({
      ...group,
      subGroups: Array.from(group.subGroups.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => a.name.localeCompare(b.name))
    })).sort((a, b) => {
      if (a.stageName !== b.stageName) return a.stageName.localeCompare(b.stageName)
      return a.groupName.localeCompare(b.groupName)
    })
  }

  // Get count of lots without groups (others)
  const getOthersCount = () => {
    if (!selectedMapForLots || !selectedMapForLots.regions) return 0
    return selectedMapForLots.regions.filter(region => {
      const isStaged = region.name && String(region.name).trim() !== ''
      // Apply status filter
      let statusMatch = true
      if (onlyLivePlans) {
        statusMatch = region.status === 'AVAILABLE'
      } else if (activeLotTab === 'available') {
        statusMatch = region.status === 'AVAILABLE'
      } else if (activeLotTab === 'sold') {
        statusMatch = region.status === 'SOLD'
      } else if (activeLotTab === 'locked') {
        statusMatch = region.status === 'LOCKED'
      }
      // For 'all' tab, statusMatch remains true
      
      return isStaged && statusMatch && !region.customProps?.group
    }).length
  }

  // Get all stages with status breakdowns
  const getStagesWithStatus = () => {
    if (!selectedMapForLots || !selectedMapForLots.regions) return []
    
    const stageMap = new Map()
    
    selectedMapForLots.regions.forEach(region => {
      const isStaged = region.name && String(region.name).trim() !== ''
      if (!isStaged) return
      
      const stageName = region.name
      if (!stageMap.has(stageName)) {
        stageMap.set(stageName, {
          name: stageName,
          available: [],
          sold: [],
          locked: []
        })
      }
      
      const stage = stageMap.get(stageName)
      if (region.status === 'AVAILABLE') {
        stage.available.push(region)
      } else if (region.status === 'SOLD') {
        stage.sold.push(region)
      } else if (region.status === 'LOCKED') {
        stage.locked.push(region)
      }
    })
    
    return Array.from(stageMap.values()).sort((a, b) => a.name.localeCompare(b.name))
  }

  const exportLotDetails = (selectedGroup = null) => {
    if (!selectedMapForLots) return
    
    let filteredRegions = selectedMapForLots.regions?.filter(region => {
      const isStaged = region.name && String(region.name).trim() !== ''
      
      // Apply status filter
      let statusMatch = true
      if (activeLotTab === 'available') statusMatch = region.status === 'AVAILABLE'
      else if (activeLotTab === 'sold') statusMatch = region.status === 'SOLD'
      else if (activeLotTab === 'locked') statusMatch = region.status === 'LOCKED'
      
      // Apply group filter if selected
      let groupMatch = true
      if (selectedGroup) {
        if (selectedGroup.groupName === 'OTHERS') {
          groupMatch = !region.customProps?.group
        } else {
          groupMatch = region.customProps?.group === selectedGroup.groupName
          if (groupMatch && selectedGroup.stageName) {
            groupMatch = region.name === selectedGroup.stageName
          }
          if (groupMatch && selectedGroup.subGroupName) {
            groupMatch = region.customProps?.subGroup === selectedGroup.subGroupName
          }
        }
      }
      
      return isStaged && statusMatch && groupMatch
    }) || []
    
    // If exporting by groups, add stage and group columns
    const includeGroupColumns = selectedGroup || exportModal?.type === 'group'
    
    const columns = includeGroupColumns ? [
      { key: 'stageName', label: 'Stage Name', formatter: (value, item) => item.name || '' },
      { key: 'groupName', label: 'Sales Group', formatter: (value, item) => item.customProps?.group || 'No Sales Group' },
      { key: 'subGroupName', label: 'Sub-Group', formatter: (value, item) => item.customProps?.subGroup || 'No Sub-Group' },
      { key: 'lotNumber', label: 'Lot Number', formatter: (value, item, index) => value || `Lot ${index + 1}` },
      { key: 'lotSize', label: 'Size' },
      { key: 'price', label: 'Price', formatter: formatCurrency },
      { key: 'status', label: 'Status', formatter: formatStatus },
      { key: 'frontage', label: 'Frontage', formatter: (value, item) => item.customProps?.frontage || '' },
      { key: 'aspect', label: 'Aspect', formatter: (value, item) => item.customProps?.aspect || '' }
    ] : [
      { key: 'lotNumber', label: 'Lot Number', formatter: (value, item, index) => value || `Lot ${index + 1}` },
      { key: 'lotName', label: 'Stage Name', formatter: (value, item) => value || item.name || '' },
      { key: 'lotSize', label: 'Size' },
      { key: 'price', label: 'Price', formatter: formatCurrency },
      { key: 'status', label: 'Status', formatter: formatStatus },
      { key: 'frontage', label: 'Frontage', formatter: (value, item) => item.customProps?.frontage || '' },
      { key: 'aspect', label: 'Aspect', formatter: (value, item) => item.customProps?.aspect || '' }
    ]
    
    // If exporting by groups, sort by stage, group, sub-group, then lot number
    if (includeGroupColumns) {
      filteredRegions = [...filteredRegions].sort((a, b) => {
        const aStage = a.name || ''
        const bStage = b.name || ''
        const aGroup = a.customProps?.group || 'No Sales Group'
        const bGroup = b.customProps?.group || 'No Sales Group'
        const aSubGroup = a.customProps?.subGroup || 'No Sub-Group'
        const bSubGroup = b.customProps?.subGroup || 'No Sub-Group'
        
        if (aStage !== bStage) return aStage.localeCompare(bStage)
        if (aGroup !== bGroup) return aGroup.localeCompare(bGroup)
        if (aSubGroup !== bSubGroup) return aSubGroup.localeCompare(bSubGroup)
        const aNum = parseInt(a.lotNumber) || 0
        const bNum = parseInt(b.lotNumber) || 0
        return aNum - bNum
      })
    }
    
    const csvContent = arrayToCSV(filteredRegions, columns)
    let groupSuffix = ''
    if (selectedGroup) {
      if (selectedGroup.groupName === 'OTHERS') {
        groupSuffix = '-others-no-group'
      } else {
        groupSuffix = `-${selectedGroup.stageName}-SalesGroup-${selectedGroup.groupName}`
        if (selectedGroup.subGroupName) {
          groupSuffix += `-SubGroup-${selectedGroup.subGroupName}`
        }
      }
    } else if (includeGroupColumns) {
      groupSuffix = '-by-sales-groups'
    }
    const filename = `lot-details${groupSuffix}-${selectedMapForLots.name.replace(/[^a-zA-Z0-9]/g, '-')}-${activeLotTab}-${new Date().toISOString().split('T')[0]}.csv`
    downloadCSV(csvContent, filename)
    setExportModal(null)
  }

  const handleExportClick = () => {
    setExportModal({ type: 'default', selectedGroup: null })
  }

  const handleExportConfirm = () => {
    if (exportModal?.type === 'group' && exportModal?.selectedGroup) {
      exportLotDetails(exportModal.selectedGroup)
    } else if (exportModal?.type === 'group') {
      // Export all groups (including others)
      exportLotDetails(null)
    } else {
      // Default export
      exportLotDetails(null)
    }
  }

  const exportEnquiries = () => {
    const columns = [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'lotNumber', label: 'Lot Number' },
      { key: 'lotName', label: 'Stage/Lot Name' },
      { key: 'lotSize', label: 'Lot Size' },
      { key: 'lotPrice', label: 'Lot Price', formatter: formatCurrency },
      { key: 'frontage', label: 'Frontage' },
      { key: 'aspect', label: 'Aspect' },
      { key: 'status', label: 'Status', formatter: formatStatus },
      { key: 'submittedAt', label: 'Submitted Date', formatter: formatDate },
      { key: 'mapName', label: 'Plan Name', formatter: (value, item) => item.map?.name || '' }
    ]
    
    const csvContent = arrayToCSV(enquiries, columns)
    const filename = `enquiries-${new Date().toISOString().split('T')[0]}.csv`
    downloadCSV(csvContent, filename)
  }

  if (loading) {
    return (
      <div className="app-bg" style={{ minHeight: '100vh', padding: '16px 16px 16px 0' }}>
        <div className="glass glass-card" style={{ margin: 12 }}>
          <div className="row-between" style={{ marginBottom: 16 }}>
            <div className="skeleton skeleton-line tall" style={{ width: 140 }}></div>
            <div className="skeleton skeleton-line" style={{ width: 100 }}></div>
          </div>
          <div className="skeleton-grid">
            <div className="skeleton-card"><div className="skeleton skeleton-line" style={{ width: 80 }}></div><div className="skeleton skeleton-line tall" style={{ width: 60 }}></div></div>
            <div className="skeleton-card"><div className="skeleton skeleton-line" style={{ width: 90 }}></div><div className="skeleton skeleton-line tall" style={{ width: 70 }}></div></div>
            <div className="skeleton-card"><div className="skeleton skeleton-line" style={{ width: 110 }}></div><div className="skeleton skeleton-line tall" style={{ width: 50 }}></div></div>
            <div className="skeleton-card"><div className="skeleton skeleton-line" style={{ width: 120 }}></div><div className="skeleton skeleton-line tall" style={{ width: 80 }}></div></div>
          </div>
        </div>
        <div className="glass glass-card" style={{ margin: 12 }}>
          <div className="skeleton skeleton-line tall" style={{ width: 180, marginBottom: 12 }}></div>
          <div className="skeleton" style={{ height: 280, borderRadius: 12 }}></div>
        </div>
        <div className="glass glass-card" style={{ margin: 12 }}>
          <div className="skeleton skeleton-line tall" style={{ width: 160, marginBottom: 12 }}></div>
          <div className="skeleton" style={{ height: 220, borderRadius: 12 }}></div>
        </div>
      </div>
    )
  }

  return (
    <div className="app-bg" style={{ minHeight: '100vh', padding: '16px 16px 16px 0' }}>
      <Topbar 
        title="Admin" 
        onMenu={() => setDrawerOpen(true)} 
        onLogout={logout} 
        userEmail={user?.email}
        userRole={user?.role || 'Admin'}
        onSettingsClick={() => setCurrentView('settings')}
        logoUrl={tenant?.logoUrl ? config.getApiUrl(tenant.logoUrl) : undefined}
      />
      {isSmall && drawerOpen && (
        <>
          <div className="drawer-overlay" onClick={() => setDrawerOpen(false)}></div>
          <div className={`drawer glass open`} style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="row-between" style={{ marginBottom: 8 }}>
              <div className="title">Menu</div>
              <button className="btn btn-ghost btn-sm" aria-label="Close menu" onClick={()=>setDrawerOpen(false)}>✕</button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>{user?.email}</div>
            <button className={`btn sidebar-btn ${currentView==='dashboard'?'active':''}`} onClick={()=>{ setCurrentView('dashboard'); setDrawerOpen(false) }}>Dashboard</button>
            <button className={`btn sidebar-btn ${currentView==='lots' && lotsPublishedOnly?'active':''}`} onClick={()=>{ setOnlyLivePlans(false); setLotsPublishedOnly(true); setCurrentView('lots'); setDrawerOpen(false) }}>Lot Status</button>
            <button className={`btn sidebar-btn ${currentView==='lots' && !lotsPublishedOnly?'active':''}`} onClick={()=>{ setOnlyLivePlans(false); setLotsPublishedOnly(false); setCurrentView('lots'); setDrawerOpen(false) }}>Total lots</button>
            <button className={`btn sidebar-btn ${currentView==='lots' && !lotsPublishedOnly && onlyLivePlans?'active':''}`} onClick={()=>{ setOnlyLivePlans(true); setLotsPublishedOnly(false); setCurrentView('lots'); setDrawerOpen(false) }}>Available lots</button>
            <button className={`btn sidebar-btn ${currentView==='plan-share'?'active':''}`} onClick={()=>{ setCurrentView('plan-share'); setDrawerOpen(false) }}>Live Plan</button>
            <button className={`btn sidebar-btn ${currentView==='enquiries'?'active':''}`} onClick={()=>{ setCurrentView('enquiries'); setDrawerOpen(false) }}>Enquiries</button>
            <button className={`btn sidebar-btn ${currentView==='settings'?'active':''}`} onClick={()=>{ setCurrentView('settings'); setDrawerOpen(false) }}>Settings</button>
          </div>
        </>
      )}
        <div className="layout" style={{ marginTop: 12 }}>
        {!isSmall && (
          <div className="sidebar" style={{ padding: 0, display: 'flex', flexDirection: 'column', gap: 8, borderRight: '1px solid #e5e7eb' }}>
            <Sidebar 
              bare 
              active={currentView === 'lots' && lotsPublishedOnly ? 'maps' : currentView} 
              onSelect={(key) => {
                if (key === 'dashboard') { setOnlyLivePlans(false); setCurrentView('dashboard') }
                if (key === 'maps') { setOnlyLivePlans(false); setLotsPublishedOnly(true); setCurrentView('lots') }
                if (key === 'plan-share') { setOnlyLivePlans(false); setCurrentView('plan-share') }
                if (key === 'enquiries') { setOnlyLivePlans(false); setCurrentView('enquiries') }
                if (key === 'settings') { setOnlyLivePlans(false); setCurrentView('settings') }
              }}
            />
          </div>
        )}

        <div className="content">
          {currentView === 'dashboard' && (
            <div style={{ display: 'grid', gap: 16 }}>

              {/* Overview without card background/shadow */}
              <div style={{ display: 'grid', gap: 16 }}>
                <div className="row-between">
                  <h3 className="title">Overview</h3>
                  {/* <span className="muted" style={{ fontSize: 12 }}>Current month</span> */}
                </div>
                <div className="stat-grid">
                  <div className="stat-card stat-card-yellow" style={{ minHeight: 96, cursor: 'pointer' }} onClick={() => { setOnlyLivePlans(false); setLotsPublishedOnly(true); setCurrentView('lots') }}>
                    {/* <div className="stat-icon">🗺️</div> */}
                    <div className="stat-title">Total Plans</div>
                    <div className="stat-value">{maps.length}</div>
                  </div>
                  <div className="stat-card stat-card-yellow" style={{ minHeight: 96, cursor: 'pointer' }} onClick={() => { setOnlyLivePlans(false); setLotsPublishedOnly(false); setCurrentView('lots') }}>
                    {/* <div className="stat-icon">🏘️</div> */}
                    <div className="stat-title">Total lots</div>
                    <div className="stat-value">{maps.reduce((a, m) => a + (m.regions?.filter(r => r.name && String(r.name).trim() !== '').length || 0), 0)}</div>
                  </div>
                  <div className="stat-card stat-card-yellow" style={{ minHeight: 96, cursor: 'pointer' }} onClick={() => { setOnlyLivePlans(true); setLotsPublishedOnly(false); setCurrentView('lots') }}>
                    {/* <div className="stat-icon">✅</div> */}
                    <div className="stat-title">Available lots</div>
                    <div className="stat-value">{maps.reduce((a, m) => a + (m.regions?.filter(r => r.name && String(r.name).trim() !== '' && r.status === 'AVAILABLE').length || 0), 0)}</div>
                  </div>
                  <div className="stat-card stat-card-yellow" style={{ minHeight: 96, cursor: 'pointer' }} onClick={() => setCurrentView('enquiries')}>
                    {/* <div className="stat-icon">📧</div> */}
                    <div className="stat-title">Enquiries</div>
                    
                    <div className="stat-value">{enquiries.length}</div>
                    <div className="muted" style={{ fontSize: 10 }}> *Total Enquiries </div>
                  </div>
                </div>
              </div>

              {/* Charts Section */}
              <div style={{ display: 'grid', gap: 16 }}>
                <div className="chart-section-header">
                  <h3 className="chart-section-title">Analytics Dashboard</h3>
                  <button 
                    className="chart-refresh-btn" 
                    onClick={fetchChartData}
                    disabled={chartsLoading}
                  >
                    {chartsLoading ? 'Loading...' : 'Refresh Charts'}
                  </button>
                </div>
              {/* Plan Performance row with optional overview panel (responsive) */}
              <div className={planDrilldown ? 'plan-row drilldown' : 'plan-row'}>
                <div className={`glass glass-card chart-col ${planDrilldown ? 'half' : 'full'}`}>
                  {!planDrilldown ? (
                    (() => {
                      const perf = chartData.planPerformance || { labels: [], datasets: [], meta: {} }
                      const mapNames = perf.meta?.mapNames || perf.labels || []
                      const total = perf.datasets?.[0]?.data || []
                      const availableArr = perf.datasets?.[1]?.data || []
                      const soldArr = perf.datasets?.[2]?.data || []
                      const lockedArr = perf.datasets?.[3]?.data || []
                      const sliceCount = 5
                      const labels5 = mapNames.slice(0, sliceCount)
                      const total5 = total.slice(0, sliceCount)
                      const available5 = availableArr.slice(0, sliceCount)
                      const sold5 = soldArr.slice(0, sliceCount)
                      const locked5 = lockedArr.slice(0, sliceCount)
                      return (
                        <div>
                          <BarChart 
                            data={{ labels: labels5, datasets: [{ label: 'Total Clicks', data: total5 }] }}
                            title="Performance (Clicks)"
                            colors={['#001a3d']}
                            loading={chartsLoading}
                            horizontal={true}
                            showValueLabels={true}
                            valueFormatter={(v)=> typeof v==='number'? v.toLocaleString(): v}
                            barGap={0.25}
                            categoryGap={0.35}
                            minCategoryHeight={56}
                            onBarClick={({ index }) => {
                              const name = labels5[index]
                              const rawName = perf.meta?.mapNames?.[index] || name
                              const mapId = perf.meta?.mapIds?.[index]
                              setPlanDrilldown({ name: rawName, data: { mapId } })
                            }}
                          />
                          <div style={{ 
                            textAlign: 'center', 
                            marginTop: 12, 
                            fontSize: 12, 
                            color: '#6b7280',
                            fontStyle: 'italic'
                          }}>
                            💡 Click the blue bar to see the lot engagement report
                          </div>
                        </div>
                      )
                    })()
                  ) : (
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div className="row" style={{ alignItems: 'center', gap: 8 }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          aria-label="Back"
                          onClick={() => setPlanDrilldown(null)}
                          style={{ background: '#e5e7eb', padding: '6px 10px' }}
                        >
                          ←
                        </button>
                        <h4 className="title" style={{ margin: 0 }}>Plan: {planDrilldown.name}</h4>
                      </div>
                      {(() => {
                        const mapId = planDrilldown.data.mapId
                        // Simple inline loader while fetching
                        return (
                          <TopLotsByClicks mapId={mapId} token={token} />
                        )
                      })()}
                    </div>
                  )}
                </div>

                {planDrilldown && (
                  <div className="glass glass-card overview-col" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {drilldownMap?.backgroundUrl || drilldownMap?.backgroundSvgUrl ? (
                      <img 
                        src={drilldownMap.backgroundUrl ? config.getApiUrl(drilldownMap.backgroundUrl) : config.getApiUrl(drilldownMap.backgroundSvgUrl)} 
                        alt={`Overview of ${drilldownMap.name}`} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} 
                      />
                    ) : (
                      <div className="muted" style={{ textAlign: 'center' }}>No background image available for {planDrilldown.name}</div>
                    )}
                  </div>
                )}
              </div>

              <div className="charts-grid" style={{ marginTop: 16 }}>
                  {/* Lot Status Distribution */}
                  <div className="glass glass-card">
                    <PieChart 
                      data={chartData.lotStatus}
                      title="Lot Status Distribution"
                      height={320}
                      colors={['#55754e', '#dc2626', '#b5a07a']}
                      loading={chartsLoading}
                    />
                  </div>

                  {/* Enquiries By Month */}
                  <div className="glass glass-card">
                    {(() => {
                      const currentYear = new Date().getFullYear()
                      const et = chartData.enquiriesTimeline || { labels: [], values: [], datasetLabel: 'Enquiries' }
                      const vals = Array.isArray(et.values) ? et.values.map(v => (Number.isFinite(+v) ? +v : 0)) : []
                      const cum = vals.reduce((acc, v, i) => { acc.push((acc[i-1] || 0) + v); return acc }, [])
                      return (
                        <ComboChart
                          labels={et.labels}
                          bar={{ label: et.datasetLabel || 'Enquiries', data: vals, color: '#001a3d' }}
                          line={{ label: 'Cumulative', data: cum, color: '#c7af87' }}
                          title={`Enquiries by Month (${currentYear})`}
                          height={320}
                          loading={chartsLoading}
                        />
                      )
                    })()}
                  </div>

                  {/* Price Distribution (hidden for now as requested) */}
                </div>
              </div>
            </div>
          )}

          {currentView === 'list' && (
            <div className="glass glass-card">
              <div className="row-between" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <h3 className="title">Plan Overview</h3>
                {/* <h3 className="title" style={{ cursor: 'pointer' }} onClick={() => setCurrentView('upload')}>
                  + Upload New Plan
                </h3> */}
              </div>
              <MapList 
                maps={maps.filter(m => m.versions && m.versions.length > 0)}
                onMapSelect={handleMapSelect}
                onRefresh={fetchMaps}
              />
            </div>
          )}

          {currentView === 'plan-share' && (
            <div className="glass glass-card">
              <div className="row-between" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <h3 className="title">Live Plan</h3>
                {/* <h3 className="title" style={{ cursor: 'pointer' }} onClick={() => setCurrentView('upload')}>
                  + Upload New Plan
                </h3> */}
              </div>
              <MapList maps={maps} onMapSelect={handleMapSelect} onRefresh={fetchMaps} />
            </div>
          )}
          {currentView === 'settings' && (
            <div className="glass glass-card">
              <h3 className="title" style={{ marginBottom: 16 }}>Settings</h3>
              
              {/* Logo Upload Section */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>Company Logo</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  {tenant?.logoUrl && (
                    <div style={{ 
                      width: 120, 
                      height: 120, 
                      border: '1px solid #e5e7eb', 
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      background: '#f9fafb'
                    }}>
                      <img 
                        src={config.getApiUrl(tenant.logoUrl)} 
                        alt="Company Logo" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%', 
                          objectFit: 'contain' 
                          
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'block'
                        }}
                      />
                      <div style={{ display: 'none', color: '#6b7280', fontSize: 12 }}>Logo</div>
                    </div>
                  )}
                  <div>
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      style={{ display: 'none' }}
                      disabled={logoUploading}
                    />
                    <label 
                      htmlFor="logo-upload" 
                      className="btn"
                      style={{ 
                        cursor: logoUploading ? 'not-allowed' : 'pointer',
                        opacity: logoUploading ? 0.6 : 1,
                        display: 'inline-block'
                      }}
                    >
                      {logoUploading ? 'Uploading...' : tenant?.logoUrl ? 'Change Logo' : 'Upload Logo'}
                    </label>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      Supported formats: JPEG, PNG, SVG, GIF (max 2MB)
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>Contact Information</h4>
                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                  This information will be displayed on downloaded PDF plans.
                </p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 500 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="contact@example.com"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14,
                        outline: 'none'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                      Website
                    </label>
                    <input
                      type="url"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      placeholder="https://www.example.com"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14,
                        outline: 'none'
                      }}
                    />
                  </div>
                  
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveContactInfo}
                    disabled={savingContact}
                    style={{
                      alignSelf: 'flex-start',
                      opacity: savingContact ? 0.6 : 1,
                      cursor: savingContact ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {savingContact ? 'Saving...' : 'Save Contact Information'}
                  </button>
                </div>
              </div>

              {/* Status Colors Section */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>Status Colors</h4>
                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                  Customize the colors used for Available, Sold, and Locked lots in the viewer and dashboards.
                </p>
                {maps.length === 0 ? (
                  <div className="muted" style={{ fontSize: 13 }}>Upload a plan to configure its status colors.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 520 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                        Select Plan
                      </label>
                      <select
                        className="select"
                        value={statusColorMapId}
                        onChange={(e) => setStatusColorMapId(e.target.value)}
                        style={{ width: '100%', maxWidth: 320 }}
                      >
                        {maps.map(map => (
                          <option key={map.id} value={map.id}>{map.name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                      {(['AVAILABLE', 'SOLD', 'LOCKED']).map(status => (
                        <div key={status} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#f9fafb' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                            {status === 'AVAILABLE' ? 'Available' : status === 'SOLD' ? 'Sold' : 'Locked'}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                              type="color"
                              value={statusColorInputs[status]}
                              onChange={(e) => handleStatusColorChange(status, e.target.value)}
                              style={{
                                width: 48,
                                height: 32,
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                cursor: 'pointer'
                              }}
                            />
                            <input
                              type="text"
                              value={statusColorInputs[status]}
                              onChange={(e) => handleStatusColorChange(status, e.target.value)}
                              placeholder="#FFFFFF"
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                fontFamily: 'monospace'
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveStatusColors}
                      disabled={savingStatusColors}
                      style={{
                        alignSelf: 'flex-start',
                        opacity: savingStatusColors ? 0.6 : 1,
                        cursor: savingStatusColors ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {savingStatusColors ? 'Saving colors...' : 'Save Status Colors'}
                    </button>
                  </div>
                )}
              </div>

              {/* Viewer Theme Section */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>Viewer Theme</h4>
                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                  Choose the template style for the stages UI in the viewer.
                </p>
                {maps.length === 0 ? (
                  <div className="muted" style={{ fontSize: 13 }}>Upload a plan to configure its viewer theme.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 520 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                        Select Plan
                      </label>
                      <select
                        className="select"
                        value={stageTemplateMapId}
                        onChange={(e) => {
                          const mapId = e.target.value
                          setStageTemplateMapId(mapId)
                          const selectedMap = maps.find(m => m.id === mapId)
                          if (selectedMap) {
                            setStageTemplateInput(selectedMap.stageTemplate || 'default')
                          }
                        }}
                        style={{ width: '100%', maxWidth: 320 }}
                      >
                        {maps.map(map => (
                          <option key={map.id} value={map.id}>{map.name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: 16,
                          border: `2px solid ${stageTemplateInput === 'default' ? '#8b5cf6' : '#e5e7eb'}`,
                          borderRadius: 8,
                          background: stageTemplateInput === 'default' ? '#f3f4f6' : '#fff',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => setStageTemplateInput('default')}
                      >
                        <input
                          type="radio"
                          name="stageTemplate"
                          value="default"
                          checked={stageTemplateInput === 'default'}
                          onChange={() => setStageTemplateInput('default')}
                          style={{ margin: 0 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Default</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>Traditional stage display with standard layout</div>
                        </div>
                      </label>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: 16,
                          border: `2px solid ${stageTemplateInput === 'modern' ? '#8b5cf6' : '#e5e7eb'}`,
                          borderRadius: 8,
                          background: stageTemplateInput === 'modern' ? '#f3f4f6' : '#fff',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => setStageTemplateInput('modern')}
                      >
                        <input
                          type="radio"
                          name="stageTemplate"
                          value="modern"
                          checked={stageTemplateInput === 'modern'}
                          onChange={() => setStageTemplateInput('modern')}
                          style={{ margin: 0 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Modern</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>Contemporary design with enhanced visual appeal</div>
                        </div>
                      </label>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveStageTemplate}
                      disabled={savingStageTemplate}
                      style={{
                        alignSelf: 'flex-start',
                        opacity: savingStageTemplate ? 0.6 : 1,
                        cursor: savingStageTemplate ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {savingStageTemplate ? 'Saving theme...' : 'Save Viewer Theme'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {currentView === 'enquiries' && (
            <div className="glass glass-card">
              <div className="row-between" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <h3 className="title">Enquiries ({enquiries.length})</h3>
                <div className="row" style={{ gap: 8 }}>
                  <button className="btn btn-primary" onClick={() => window.location.href = 'https://www.zoho.com/crm/login.html'}>Redirect to CRM</button>
                  <button 
                    className="btn btn-ghost" 
                    onClick={exportEnquiries}
                    style={{ background: '#f3f4f6', border: '1px solid #d1d5db' }}
                  >
                    📊 Export CSV
                  </button>
                  {/* <button 
                    className="btn btn-sm" 
                    onClick={testCRMConnection}
                    style={{ 
                      backgroundColor: crmConnectionStatus === 'testing' ? '#f59e0b' : 
                                      crmConnectionStatus === 'connected' ? '#10b981' :
                                      crmConnectionStatus === 'error' ? '#ef4444' : '#6b7280',
                      color: 'white',
                      marginRight: 8
                    }}
                    disabled={crmConnectionStatus === 'testing'}
                  >
                    {crmConnectionStatus === 'testing' ? '⏳ Testing CRM...' : 
                     crmConnectionStatus === 'connected' ? '✓ CRM Connected' :
                     crmConnectionStatus === 'error' ? '✕ CRM Error' : '🔗 Test CRM'}
                  </button> */}
                  <button className="btn btn-primary" onClick={fetchEnquiries}>Refresh</button>
                </div>
              </div>
              
              {/* Filter Tabs */}
              {enquiries.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid #e5e7eb', paddingBottom: 8 }}>
                  <button
                    className={`btn ${enquiryFilter === 'all' ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setEnquiryFilter('all')}
                    style={{ 
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontSize: 14,
                      fontWeight: enquiryFilter === 'all' ? 600 : 400
                    }}
                  >
                    All Enquiries ({enquiries.length})
                  </button>
                  <button
                    className={`btn ${enquiryFilter === 'lot' ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setEnquiryFilter('lot')}
                    style={{ 
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontSize: 14,
                      fontWeight: enquiryFilter === 'lot' ? 600 : 400
                    }}
                  >
                    Lot Enquiries ({enquiries.filter(e => e.lotId && e.lotId !== 'GENERAL_ENQUIRY').length})
                  </button>
                  <button
                    className={`btn ${enquiryFilter === 'general' ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setEnquiryFilter('general')}
                    style={{ 
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontSize: 14,
                      fontWeight: enquiryFilter === 'general' ? 600 : 400
                    }}
                  >
                    General Enquiries ({enquiries.filter(e => e.lotId === 'GENERAL_ENQUIRY').length})
                  </button>
                </div>
              )}

              {(() => {
                // Filter enquiries based on selected filter
                const filteredEnquiries = enquiryFilter === 'all' 
                  ? enquiries 
                  : enquiryFilter === 'lot'
                  ? enquiries.filter(e => e.lotId && e.lotId !== 'GENERAL_ENQUIRY')
                  : enquiries.filter(e => e.lotId === 'GENERAL_ENQUIRY')

                return filteredEnquiries.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📝</div>
                    <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 8 }}>
                      {enquiryFilter === 'all' ? 'No enquiries yet' : 
                       enquiryFilter === 'lot' ? 'No lot enquiries yet' : 
                       'No general enquiries yet'}
                    </div>
                    <div>
                      {enquiryFilter === 'all' ? 'When visitors submit enquiries about lots, they\'ll appear here.' :
                       enquiryFilter === 'lot' ? 'Enquiries about specific lots will appear here.' :
                       'General enquiries (not about a specific lot) will appear here.'}
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 12px 0', color: '#6b7280', fontSize: 12 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span className="dot dot-success"></span> Contacted
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span className="dot dot-warning"></span> New
                      </span>
                    </div>
                    <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th style={{ width: isSmall ? '20%' : '16%' }}>Contact Name</th>
                          <th style={{ width: isSmall ? '24%' : '20%' }}>Email / Phone</th>
                          <th style={{ width: isSmall ? '26%' : '26%', textAlign: 'center' }}>Lot Details</th>
                          {isSmall && (<th style={{ width: '10%', textAlign: 'center' }}>Status</th>)}
                          <th style={{ width: isSmall ? '10%' : '10%' }}>Date</th>
                          <th style={{ width: isSmall ? '12%' : '16%', textAlign: 'center' }}>Message</th>
                          <th style={{ width: isSmall ? '8%' : '12%', textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEnquiries.map((enquiry) => (
                        <tr key={enquiry.id}>
                          <td data-label="Contact Name">
                            {(() => {
                              const s = enquiry.status || 'NEW'
                              const bg = s==='NEW'? '#FEF3C7' : s==='CONTACTED'? '#DCFCE7' : s==='INTERESTED'? '#DBEAFE' : s==='NOT_INTERESTED'? '#FFE4E6' : '#E5E7EB'
                              const color = s==='NEW'? '#92400E' : s==='CONTACTED'? '#065F46' : s==='INTERESTED'? '#1E40AF' : s==='NOT_INTERESTED'? '#991B1B' : '#374151'
                              return (
                                <div className="table-primary" style={{ marginBottom: 2 }}>
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: !isSmall ? bg : 'transparent', color: !isSmall ? color : 'inherit' }}>
                                    {enquiry.name || '—'}
                                  </span>
                                </div>
                              )
                            })()}
                          </td>
                          <td data-label="Email / Phone">
                            <div className="muted" style={{ fontSize: 12, lineHeight: 1.3 }}>
                              {enquiry.email || '—'}{enquiry.phone ? '' : ''}
                              {enquiry.phone && (<><br />{enquiry.phone}</>)}
                            </div>
                          </td>
                          <td data-label="Lot Details">
                            {(() => {
                              // Check if this is a general enquiry
                              const isGeneralEnquiry = enquiry.lotId === 'GENERAL_ENQUIRY'
                              
                              if (isGeneralEnquiry) {
                                return (
                                  <div className="muted" style={{ fontSize: 12, fontStyle: 'italic', color: '#6b7280' }}>
                                    General Enquiry
                                  </div>
                                )
                              }
                              
                              const lotText = [
                                enquiry.lotNumber ? `Lot: ${enquiry.lotNumber}` : '',
                                enquiry.lotName ? `Stage: ${enquiry.lotName}` : '',
                                enquiry.lotSize ? `Size: ${enquiry.lotSize}` : '',
                                enquiry.lotPrice ? `Price: $${Number(enquiry.lotPrice).toLocaleString()}` : ''
                              ].filter(Boolean).join(' ')
                              const hasAny = lotText.length > 0
                              const expanded = expandedLotIds.has(enquiry.id)
                              return (
                                <>
                                  <div 
                                    className="muted" 
                                    style={{ 
                                      fontSize: 12,
                                      whiteSpace: expanded ? 'pre-wrap' : 'nowrap',
                                      overflow: expanded ? 'visible' : 'hidden',
                                      textOverflow: expanded ? 'clip' : 'ellipsis',
                                      wordBreak: expanded ? 'break-word' : 'normal'
                                    }}
                                  >
                                    {hasAny ? lotText : '—'}
                                  </div>
                                  {hasAny && lotText.length > 40 && (
                                    <button
                                      type="button"
                                      className="link-btn"
                                      style={{ marginTop: 6 }}
                                      onClick={() => {
                                        setExpandedLotIds(prev => {
                                          const next = new Set(prev)
                                          if (next.has(enquiry.id)) next.delete(enquiry.id); else next.add(enquiry.id)
                                          return next
                                        })
                                      }}
                                    >
                                      {expanded ? 'Show less' : 'Read more'}
                                    </button>
                                  )}
                                </>
                              )
                            })()}
                          </td>
                          {isSmall && (
                            <td data-label="Status" style={{ textAlign: 'center' }}>
                              {enquiry.status === 'NEW' ? (
                                <span className="badge"><span className="dot dot-warning"></span> NEW</span>
                              ) : (
                                <span className="badge badge-success"><span className="dot dot-success"></span> CONTACTED</span>
                              )}
                            </td>
                          )}
                          <td data-label="Date">{new Date(enquiry.submittedAt).toLocaleDateString()}</td>
                          <td data-label="Message">
                            <div 
                              className="muted" 
                              style={{ 
                                fontSize: 12, 
                                textAlign: 'center',
                                whiteSpace: expandedMsgIds.has(enquiry.id) ? 'pre-wrap' : 'nowrap',
                                overflow: expandedMsgIds.has(enquiry.id) ? 'visible' : 'hidden',
                                textOverflow: expandedMsgIds.has(enquiry.id) ? 'clip' : 'ellipsis',
                                wordBreak: expandedMsgIds.has(enquiry.id) ? 'break-word' : 'normal'
                              }}
                            >
                              {enquiry.message || '—'}
                            </div>
                            {enquiry.message && enquiry.message.length > 60 && (
                              <button
                                type="button"
                                className="link-btn"
                                style={{ marginTop: 6 }}
                                onClick={() => {
                                  setExpandedMsgIds(prev => {
                                    const next = new Set(prev)
                                    if (next.has(enquiry.id)) next.delete(enquiry.id); else next.add(enquiry.id)
                                    return next
                                  })
                                }}
                              >
                                {expandedMsgIds.has(enquiry.id) ? 'Show less' : 'Read more'}
                              </button>
                            )}
                          </td>
                          <td data-label="Actions" style={{ textAlign: 'center' }}>
                            <div className="row gap-8" style={{ justifyContent: 'center' }}>
                              <button 
                                className="icon-btn btn-edit btn-sm"
                                title={enquiry.status === 'NEW' ? 'Mark as Contacted' : 'Mark as New'}
                                aria-label={enquiry.status === 'NEW' ? 'Mark as Contacted' : 'Mark as New'}
                                onClick={() => {
                                  const nextStatus = enquiry.status === 'NEW' ? 'CONTACTED' : 'NEW'
                                  fetch(`${config.getApiUrl('/enquiries')}/${enquiry.id}/status`, {
                                    method: 'PUT',
                                    headers: { 
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${token}`
                                    },
                                    body: JSON.stringify({ status: nextStatus })
                                  }).then(() => fetchEnquiries())
                                }}
                              >
                                ✓
                              </button>
                              {/* Sync to CRM button removed because auto-sync is enabled */}
                              <button 
                                className="icon-btn btn-delete btn-sm"
                                title="Delete"
                                aria-label="Delete"
                                onClick={() => {
                                  alert.confirm(
                                    'Are you sure you want to delete this enquiry?',
                                    'Delete Enquiry',
                                    () => {
                                      fetch(`${config.getApiUrl('/enquiries')}/${enquiry.id}`, {
                                        method: 'DELETE',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      }).then(() => fetchEnquiries())
                                    }
                                  )
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
                )
              })()}
            </div>
          )}

          {currentView === 'lots' && (
            <div className="glass glass-card">
              <div className="row-between" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <h3 className="title">{lotsPublishedOnly ? 'Lot Status' : (onlyLivePlans ? 'Available lots' : 'Total lots')}</h3>
                <div className="row" style={{ gap: 8 }}>
                  <button 
                    className="btn btn-ghost" 
                    onClick={lotsPublishedOnly || onlyLivePlans ? exportLivePlans : exportLotsInSystem}
                    style={{ background: '#f3f4f6', border: '1px solid #d1d5db' }}
                  >
                    📊 Export CSV
                  </button>
                  <button className="btn btn-primary" onClick={fetchMaps}>Refresh</button>
                </div>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: lotsPublishedOnly ? '24%' : '26%', textAlign: 'left' }}>Map Name</th>
                      <th style={{ width: lotsPublishedOnly ? '16%' : '26%', textAlign: 'center' }}>Total Lots</th>
                      <th style={{ width: lotsPublishedOnly ? '16%' : '26%', textAlign: 'center' }}>Available Lots</th>
                      {lotsPublishedOnly && (<th style={{ width: '14%', textAlign: 'center' }}>Sold Lots</th>)}
                      {lotsPublishedOnly && (<th style={{ width: '14%', textAlign: 'center' }}>Locked Lots</th>)}
                      <th style={{ width: lotsPublishedOnly ? '18%' : '20%', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {maps
                      .filter(map => (lotsPublishedOnly || onlyLivePlans) ? (map.versions && map.versions.length > 0) : true)
                      .map((map) => (
                      <tr key={map.id}>
                        <td data-label="Map Name" style={{ textAlign: 'left' }}>
                          <div className="table-primary" style={{ marginBottom: 2 }}>{map.name}</div>
                          {/* <div className="muted" style={{ fontSize: 12 }}>Created: {new Date(map.createdAt).toLocaleDateString()}</div> */}
                        </td>
                        <td data-label="Total Lots" style={{ textAlign: 'center' }}>
                          <div className="stat-value" style={{ fontSize: 18, fontWeight: 600 }}>{map.regions?.filter(r => r.name && String(r.name).trim() !== '').length || 0}</div>
                        </td>
                        <td data-label="Available Lots" style={{ textAlign: 'center' }}>
                          <div className="stat-value" style={{ fontSize: 18, fontWeight: 600, color: '#10b981' }}>
                            {map.regions?.filter(region => region.name && String(region.name).trim() !== '' && region.status === 'AVAILABLE').length || 0}
                          </div>
                        </td>
                        {lotsPublishedOnly && (
                          <td data-label="Sold Lots" style={{ textAlign: 'center' }}>
                            <div className="stat-value" style={{ fontSize: 18, fontWeight: 600, color: '#dc2626' }}>
                              {map.regions?.filter(region => region.name && String(region.name).trim() !== '' && region.status === 'SOLD').length || 0}
                            </div>
                          </td>
                        )}
                        {lotsPublishedOnly && (
                          <td data-label="Locked Lots" style={{ textAlign: 'center' }}>
                            <div className="stat-value" style={{ fontSize: 18, fontWeight: 600, color: '#d97706' }}>
                              {map.regions?.filter(region => region.name && String(region.name).trim() !== '' && region.status === 'LOCKED').length || 0}
                            </div>
                          </td>
                        )}
                        <td data-label="Actions" style={{ textAlign: 'right' }}>
                          <button 
                            className="btn btn-sm"
                            onClick={() => {
                              setSelectedMapForLots(map)
                              setActiveLotTab(onlyLivePlans ? 'available' : 'all')
                              setSelectedGroupFilter(null) // Reset group filter when switching maps
                              setCurrentView('lot-details')
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}


          {currentView === 'lot-details' && selectedMapForLots && (
            <div className="glass glass-card">
              <div className="row-between" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <div className="row" style={{ alignItems: 'center', gap: 12 }}>
                  <button 
                    className="btn btn-ghost" 
                    onClick={() => setCurrentView('lots')}
                    style={{ background: '#e5e7eb' }}
                  >
                    ← Back to Lots
                  </button>
                  {onlyLivePlans && (
                    <h3 className="title" style={{ margin: 0 }}>Available Lots: {selectedMapForLots.regions?.filter(r => r.name && String(r.name).trim() !== '' && r.status === 'AVAILABLE').length || 0}</h3>
                  )}
                </div>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <button 
                    className="btn btn-ghost" 
                    onClick={handleExportClick}
                    style={{ background: '#f3f4f6', border: '1px solid #d1d5db' }}
                  >
                    📊 Export CSV
                  </button>
                  <button className="btn btn-primary" onClick={fetchMaps}>Refresh</button>
                </div>
              </div>
              

              {/* Stage-based view (when not onlyLivePlans) */}
              {!onlyLivePlans && (() => {
                const stages = getStagesWithStatus()
                const currentStage = stages.find(s => s.name === selectedStage)
                const stageLots = currentStage ? [...currentStage.available, ...currentStage.sold, ...currentStage.locked] : []
                const groupMap = new Map()

                stageLots.forEach(region => {
                  const groupName = region.customProps?.group || null
                  const key = groupName || 'OTHERS'
                  if (!groupMap.has(key)) {
                    groupMap.set(key, {
                      value: groupName || 'OTHERS',
                      label: groupName || 'No Group',
                      count: 0,
                      subGroups: new Map()
                    })
                  }
                  const entry = groupMap.get(key)
                  entry.count += 1
                  if (groupName && region.customProps?.subGroup) {
                    const subName = region.customProps.subGroup
                    entry.subGroups.set(subName, (entry.subGroups.get(subName) || 0) + 1)
                  }
                })

                const stageGroups = Array.from(groupMap.values()).map(entry => ({
                  value: entry.value,
                  label: entry.label,
                  count: entry.count,
                  subGroups: Array.from(entry.subGroups.entries())
                    .map(([name, count]) => ({ name, count }))
                    .sort((a, b) => a.name.localeCompare(b.name))
                })).sort((a, b) => a.label.localeCompare(b.label))

                const activeGroupFilter = selectedGroupFilter && selectedGroupFilter.stageName === selectedStage ? selectedGroupFilter : null

                const matchesGroupFilter = (region) => {
                  if (!activeGroupFilter) return true
                  const regionGroup = region.customProps?.group || null
                  if (activeGroupFilter.groupName === 'OTHERS') {
                    return !regionGroup
                  }
                  if (regionGroup !== activeGroupFilter.groupName) return false
                  if (activeGroupFilter.subGroupName) {
                    return region.customProps?.subGroup === activeGroupFilter.subGroupName
                  }
                  return true
                }

                const filteredAvailable = currentStage ? currentStage.available.filter(matchesGroupFilter) : []
                const filteredSold = currentStage ? currentStage.sold.filter(matchesGroupFilter) : []
                const filteredLocked = currentStage ? currentStage.locked.filter(matchesGroupFilter) : []
                // Combine all lots into a single array with status
                const allFilteredLots = [
                  ...filteredAvailable.map(r => ({ ...r, __status: 'AVAILABLE' })),
                  ...filteredSold.map(r => ({ ...r, __status: 'SOLD' })),
                  ...filteredLocked.map(r => ({ ...r, __status: 'LOCKED' }))
                ].sort((a, b) => {
                  // Sort by lot number first
                  const aNum = parseInt(a.lotNumber) || 0
                  const bNum = parseInt(b.lotNumber) || 0
                  if (aNum !== bNum) {
                    return aNum - bNum
                  }
                  // If lot numbers are the same (or both 0), sort by status as secondary
                  const statusOrder = { 'AVAILABLE': 1, 'SOLD': 2, 'LOCKED': 3 }
                  return statusOrder[a.__status] - statusOrder[b.__status]
                })
                const selectedGroupEntry = activeGroupFilter ? stageGroups.find(entry => entry.value === activeGroupFilter.groupName) : null
                
                // Include unassigned groups in selectableGroups (avoid duplicates)
                const existingGroupValues = new Set(stageGroups.filter(entry => entry.value !== 'OTHERS').map(entry => entry.value))
                const selectableGroups = [
                  ...stageGroups.filter(entry => entry.value !== 'OTHERS'),
                  ...Array.from(unassignedGroups)
                    .filter(groupName => !existingGroupValues.has(groupName))
                    .map(groupName => ({
                      value: groupName,
                      label: groupName,
                      count: 0,
                      subGroups: []
                    }))
                ].sort((a, b) => a.label.localeCompare(b.label))
                
                // Find group entry (from stageGroups or unassigned)
                const quickEditGroupEntry = quickEdit && quickEdit.stageName === selectedStage ? (
                  stageGroups.find(entry => entry.value === quickEdit.group) || 
                  (unassignedGroups.has(quickEdit.group) ? {
                    value: quickEdit.group,
                    label: quickEdit.group,
                    count: 0,
                    subGroups: []
                  } : null)
                ) : null
                
                // Include unassigned sub-groups in quickEditSubGroupOptions (avoid duplicates)
                const quickEditSubGroupOptions = quickEditGroupEntry && quickEdit.group ? (() => {
                  const existingSubNames = new Set(quickEditGroupEntry.subGroups.map(sub => sub.name))
                  const unassignedSubs = unassignedSubGroups.get(quickEdit.group)
                  const unassignedSubOptions = unassignedSubs 
                    ? Array.from(unassignedSubs)
                        .filter(subName => !existingSubNames.has(subName))
                        .map(subName => ({ name: subName, count: 0 }))
                    : []
                  return [...quickEditGroupEntry.subGroups, ...unassignedSubOptions].sort((a, b) => a.name.localeCompare(b.name))
                })() : []
                
                // Apply search filter to lots
                const searchFilteredLots = searchTerm.trim() === '' ? allFilteredLots : allFilteredLots.filter(region => {
                  const term = searchTerm.toLowerCase()
                  const regionStatus = region.__status || region.status
                  
                  // Search in lot number
                  if (region.lotNumber && region.lotNumber.toLowerCase().includes(term)) return true
                  
                  // Search in lock reason (for LOCKED lots)
                  if (regionStatus === 'LOCKED' && region.customProps?.lockReason && region.customProps.lockReason.toLowerCase().includes(term)) return true
                  
                  // Search in group
                  if (region.customProps?.group && region.customProps.group.toLowerCase().includes(term)) return true
                  
                  // Search in sub-group
                  if (region.customProps?.subGroup && region.customProps.subGroup.toLowerCase().includes(term)) return true
                  
                  // Search in size
                  if (region.lotSize && region.lotSize.toLowerCase().includes(term)) return true
                  
                  // Search in price
                  if (region.price && (String(region.price).includes(term) || `$${Number(region.price).toLocaleString()}`.toLowerCase().includes(term))) return true
                  
                  // Search in frontage
                  if (region.customProps?.frontage && region.customProps.frontage.toLowerCase().includes(term)) return true
                  
                  // Search in aspect
                  if (region.customProps?.aspect && region.customProps.aspect.toLowerCase().includes(term)) return true
                  
                  // Search in status
                  if (regionStatus && regionStatus.toLowerCase().includes(term)) return true
                  
                  return false
                })
                
                const hasFilteredLots = searchFilteredLots.length > 0

                return (
                  <div style={{ marginBottom: 16 }}>
                    {/* Stage Tabs */}
                <div className="tab-nav" style={{ marginBottom: 16, borderBottom: '1px solid #e5e7eb' }}>
                      {stages.map((stage, stageIdx) => {
                        const totalLots = stage.available.length + stage.sold.length + stage.locked.length
                        const isSelected = selectedStage === stage.name
                        // Format stage name: if it's just a number, prefix with "Stage "
                        const displayName = /^\d+$/.test(stage.name.trim()) ? `Stage ${stage.name.trim()}` : stage.name

                        return (
                  <button 
                            key={stageIdx}
                            className={`tab-btn ${isSelected ? 'active' : ''}`}
                    onClick={() => {
                              setSelectedStage(stage.name)
                              setSelectedGroupFilter(null)
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}
                          >
                            <span>{displayName} ({totalLots})</span>
                  </button>
                        )
                      })}
                    </div>

                    {currentStage && (
                      <>
                        {stageGroups.length > 0 && (
                          <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                              <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                                Filter by Group:
                              </label>
                              {activeGroupFilter && (
                  <button 
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => setSelectedGroupFilter(null)}
                                  style={{ padding: '4px 12px', fontSize: 12 }}
                                >
                                  Clear Filter
                                </button>
                              )}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              <button
                                className={`tab-btn ${!activeGroupFilter ? 'active' : ''}`}
                                onClick={() => setSelectedGroupFilter(null)}
                                style={{
                                  padding: '8px 16px',
                                  borderRadius: 6,
                                  border: '1px solid #e5e7eb',
                                  background: !activeGroupFilter ? '#3b82f6' : '#fff',
                                  color: !activeGroupFilter ? '#fff' : '#374151',
                                  fontSize: 14,
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                              >
                                All Groups
                              </button>
                              {stageGroups.map((group, idx) => {
                                const isSelected = activeGroupFilter && activeGroupFilter.groupName === group.value
                                return (
                                  <button
                                    key={idx}
                                    className={`tab-btn ${isSelected ? 'active' : ''}`}
                                    onClick={() => setSelectedGroupFilter({ stageName: selectedStage, groupName: group.value, subGroupName: null })}
                                    style={{
                                      padding: '8px 16px',
                                      borderRadius: 6,
                                      border: '1px solid #e5e7eb',
                                      background: isSelected ? '#3b82f6' : '#fff',
                                      color: isSelected ? '#fff' : '#374151',
                                      fontSize: 14,
                                      fontWeight: 500,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8
                                    }}
                                  >
                                    <span>{group.label}</span>
                                    <span style={{
                                      fontSize: 12,
                                      opacity: 0.8,
                                      background: isSelected ? 'rgba(255,255,255,0.2)' : '#f3f4f6',
                                      padding: '2px 6px',
                                      borderRadius: 4
                                    }}>
                                      {group.count}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                            {selectedGroupEntry && selectedGroupEntry.subGroups.length > 0 && activeGroupFilter?.groupName !== 'OTHERS' && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                                  Sub-Groups:
                                </span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                  <button
                                    className={`tab-btn ${!activeGroupFilter?.subGroupName ? 'active' : ''}`}
                                    onClick={() => setSelectedGroupFilter(prev => prev ? { ...prev, subGroupName: null } : prev)}
                                    style={{
                                      padding: '6px 14px',
                                      borderRadius: 6,
                                      border: '1px solid #e5e7eb',
                                      background: !activeGroupFilter?.subGroupName ? '#3b82f6' : '#fff',
                                      color: !activeGroupFilter?.subGroupName ? '#fff' : '#374151',
                                      fontSize: 13,
                                      fontWeight: 500,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    All {selectedGroupEntry.label}
                                  </button>
                                  {selectedGroupEntry.subGroups.map((subGroup, subIdx) => {
                                    const isSubSelected = activeGroupFilter?.subGroupName === subGroup.name
                                    return (
                                      <button
                                        key={subIdx}
                                        className={`tab-btn ${isSubSelected ? 'active' : ''}`}
                                        onClick={() => setSelectedGroupFilter(prev => prev ? { ...prev, subGroupName: subGroup.name } : prev)}
                                        style={{
                                          padding: '6px 14px',
                                          borderRadius: 6,
                                          border: '1px solid #e5e7eb',
                                          background: isSubSelected ? '#3b82f6' : '#fff',
                                          color: isSubSelected ? '#fff' : '#374151',
                                          fontSize: 13,
                                          fontWeight: 500,
                                          cursor: 'pointer',
                                          transition: 'all 0.2s',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 6
                                        }}
                                      >
                                        <span>{subGroup.name}</span>
                                        <span style={{
                                          fontSize: 11,
                                          opacity: 0.8,
                                          background: isSubSelected ? 'rgba(255,255,255,0.2)' : '#f3f4f6',
                                          padding: '2px 6px',
                                          borderRadius: 4
                                        }}>
                                          {subGroup.count}
                                        </span>
                                      </button>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Search Bar */}
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ position: 'relative', maxWidth: 400 }}>
                            <input
                              type="text"
                              placeholder="Search lots by number, group, size, price..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px 12px 10px 38px',
                                fontSize: 14,
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                outline: 'none',
                                transition: 'border-color 0.2s'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                            <svg
                              style={{
                                position: 'absolute',
                                left: 12,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 18,
                                height: 18,
                                color: '#9ca3af'
                              }}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            {searchTerm && (
                              <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                  position: 'absolute',
                                  right: 8,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  padding: 4,
                                  border: 'none',
                                  background: 'transparent',
                                  cursor: 'pointer',
                                  color: '#9ca3af',
                                  fontSize: 18,
                                  lineHeight: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                aria-label="Clear search"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>

                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                          {searchFilteredLots.length > 0 && (
                            <div className="table-wrap" style={{ margin: 0 }}>
                              <table className="table" style={{ margin: 0 }}>
                                <thead>
                                  <tr>
                                    <th style={{ width: '9%' }}>Lot Number</th>
                                    <th style={{ width: '10%' }}>Lock Reason</th>
                                    <th style={{ width: '10%' }}>Sales Group</th>
                                    <th style={{ width: '10%' }}>Sub-Group</th>
                                    <th style={{ width: '9%' }}>Size</th>
                                    <th style={{ width: '9%' }}>Price</th>
                                    <th style={{ width: '8%' }}>Frontage</th>
                                    <th style={{ width: '8%' }}>Aspect</th>
                                    <th style={{ width: '8%' }}>Status</th>
                                    <th style={{ width: '6%', textAlign: 'right' }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {searchFilteredLots.map((region, index) => {
                                    const regionStatus = region.__status || region.status
                                    return (
                                      <React.Fragment key={region.id || `lot-${index}`}>
                                        <tr>
                                          <td data-label="Lot Number">
                                            <div className="table-primary" style={{ fontWeight: 600 }}>
                                              {region.lotNumber || (regionStatus === 'LOCKED' ? (region.customProps?.lockReason || 'Future Release') : `Lot ${index + 1}`)}
                                            </div>
                                          </td>
                                          <td data-label="Lock Reason">
                                            <div className="muted" style={{ fontSize: 14 }}>
                                              {regionStatus === 'LOCKED' ? (
                                                region.customProps?.lockReason ? (
                                                  <span style={{
                                                    display: 'inline-block',
                                                    padding: '2px 8px',
                                                    borderRadius: 4,
                                                    background: '#fef3c7',
                                                    color: '#92400e',
                                                    fontSize: 12,
                                                    fontWeight: 500
                                                  }}>
                                                    {region.customProps.lockReason}
                                                  </span>
                                                ) : (region.lotNumber ? '—' : (
                                                  <span style={{
                                                    display: 'inline-block',
                                                    padding: '2px 8px',
                                                    borderRadius: 4,
                                                    background: '#fef3c7',
                                                    color: '#92400e',
                                                    fontSize: 12,
                                                    fontStyle: 'italic'
                                                  }}>
                                                    Future Release
                                                  </span>
                                                ))
                                              ) : '—'}
                                            </div>
                                          </td>
                                          <td data-label="Sales Group">
                                            <div className="muted" style={{ fontSize: 14 }}>
                                              {region.customProps?.group ? (
                                                <span style={{
                                                  display: 'inline-block',
                                                  padding: '2px 8px',
                                                  borderRadius: 4,
                                                  background: '#f3e8ff',
                                                  color: '#7c3aed',
                                                  fontSize: 12,
                                                  fontWeight: 500
                                                }}>
                                                  {region.customProps.group}
                                                </span>
                                              ) : '—'}
                                            </div>
                                          </td>
                                          <td data-label="Sub-Group">
                                            <div className="muted" style={{ fontSize: 14 }}>
                                              {region.customProps?.subGroup ? (
                                                <span style={{
                                                  display: 'inline-block',
                                                  padding: '2px 8px',
                                                  borderRadius: 4,
                                                  background: '#e0f2fe',
                                                  color: '#0369a1',
                                                  fontSize: 12,
                                                  fontWeight: 500
                                                }}>
                                                  {region.customProps.subGroup}
                                                </span>
                                              ) : '—'}
                                            </div>
                                          </td>
                                          <td data-label="Size">
                                            <div className="muted" style={{ fontSize: 14 }}>
                                              {region.lotSize || '—'}
                                            </div>
                                          </td>
                                          <td data-label="Price">
                                            <div className="muted" style={{ fontSize: 14 }}>
                                              {region.price ? `$${Number(region.price).toLocaleString()}` : '—'}
                                            </div>
                                          </td>
                                          <td data-label="Frontage">
                                            <div className="muted" style={{ fontSize: 12 }}>
                                              {region.customProps?.frontage || '—'}
                                            </div>
                                          </td>
                                          <td data-label="Aspect">
                                            <div className="muted" style={{ fontSize: 12 }}>
                                              {region.customProps?.aspect || '—'}
                                            </div>
                                          </td>
                                          <td data-label="Status">
                                            <div className="muted" style={{ fontSize: 14 }}>
                                              {regionStatus === 'AVAILABLE' && (
                                                <span style={{
                                                  display: 'inline-block',
                                                  padding: '2px 8px',
                                                  borderRadius: 4,
                                                  background: '#d1fae5',
                                                  color: '#065f46',
                                                  fontSize: 12,
                                                  fontWeight: 600
                                                }}>
                                                  Available
                                                </span>
                                              )}
                                              {regionStatus === 'SOLD' && (
                                                <span style={{
                                                  display: 'inline-block',
                                                  padding: '2px 8px',
                                                  borderRadius: 4,
                                                  background: '#fee2e2',
                                                  color: '#991b1b',
                                                  fontSize: 12,
                                                  fontWeight: 600
                                                }}>
                                                  Sold
                                                </span>
                                              )}
                                              {regionStatus === 'LOCKED' && (
                                                <span style={{
                                                  display: 'inline-block',
                                                  padding: '2px 8px',
                                                  borderRadius: 4,
                                                  background: '#fef3c7',
                                                  color: '#92400e',
                                                  fontSize: 12,
                                                  fontWeight: 600
                                                }}>
                                                  Locked
                                                </span>
                                              )}
                                            </div>
                                          </td>
                                          <td data-label="Actions" style={{ textAlign: 'right' }}>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                              <button
                                                className="btn btn-ghost btn-sm"
                                                onClick={() => openQuickEdit(region)}
                                                style={{ padding: '4px 8px' }}
                                                title="Quick edit"
                                                aria-label="Quick edit lot"
                                                disabled={quickEditSaving && quickEdit?.regionId === region.id}
                                              >
                                                ⚡
                                              </button>
                                              <button
                                                className="btn btn-sm"
                    onClick={() => {
                                                  setQuickEdit(null)
                                                  const actualIndex = selectedMapForLots.regions.findIndex(r => r.id === region.id)
                                                  setEditModal({ map: selectedMapForLots, regionIndex: actualIndex })
                                                }}
                                                style={{ padding: '4px 8px', minWidth: 'auto' }}
                                                title="Edit lot details"
                                              >
                                                ✏️
                  </button>
                                            </div>
                                          </td>
                                        </tr>
                                        {quickEdit && quickEdit.regionId === region.id && quickEdit.stageName === selectedStage && (
                                          <tr className="quick-edit-row">
                                            <td colSpan={10} style={{ background: '#f9fafb', padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
                                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                                                <div style={{ minWidth: 160 }}>
                                                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Status</label>
                                                  <select
                                                    value={quickEdit.status}
                                                    onChange={(e) => updateQuickEditField('status', e.target.value)}
                                                    disabled={quickEditSaving}
                                                    style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, minWidth: 150 }}
                                                  >
                                                    {statusOptions.map(status => (
                                                      <option key={status} value={status}>{formatStatus(status)}</option>
                                                    ))}
                                                  </select>
                                                </div>
                                                {quickEdit.status === STATUS.LOCKED && (
                                                  <div style={{ minWidth: 200 }}>
                                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                                                      Lock Reason / Future Release
                                                    </label>
                                                    <input
                                                      type="text"
                                                      value={quickEdit.lockReason || ''}
                                                      onChange={(e) => updateQuickEditField('lockReason', e.target.value)}
                                                      disabled={quickEditSaving}
                                                      placeholder="e.g., Future Release, Under Development..."
                                                      style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, width: '100%' }}
                                                    />
                                                  </div>
                                                )}
                                                <div style={{ minWidth: 200 }}>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Group</label>
                  <button 
                                                      className="btn btn-ghost btn-sm"
                    onClick={() => {
                                                        setCreatingGroup(true)
                                                        setNewGroupName('')
                                                      }}
                                                      disabled={quickEditSaving}
                                                      style={{ padding: '2px 6px', fontSize: 10, minWidth: 'auto' }}
                                                      title="Create new group"
                                                    >
                                                      + New
                  </button>
                                                  </div>
                                                  {creatingGroup ? (
                                                    <div style={{ display: 'flex', gap: 4 }}>
                                                      <input
                                                        type="text"
                                                        value={newGroupName}
                                                        onChange={(e) => setNewGroupName(e.target.value)}
                                                        onKeyDown={(e) => {
                                                          if (e.key === 'Enter') {
                                                            handleCreateGroup()
                                                          } else if (e.key === 'Escape') {
                                                            setCreatingGroup(false)
                                                            setNewGroupName('')
                                                          }
                                                        }}
                                                        placeholder="Group name..."
                                                        autoFocus
                                                        style={{ flex: 1, padding: '6px 12px', borderRadius: 6, border: '1px solid #c084fc', fontSize: 13 }}
                                                      />
                  <button 
                                                        className="btn btn-primary btn-sm"
                                                        onClick={handleCreateGroup}
                                                        style={{ padding: '6px 12px', fontSize: 11 }}
                                                      >
                                                        ✓
                                                      </button>
                                                      <button
                                                        className="btn btn-ghost btn-sm"
                    onClick={() => {
                                                          setCreatingGroup(false)
                                                          setNewGroupName('')
                                                        }}
                                                        style={{ padding: '6px 12px', fontSize: 11 }}
                                                      >
                                                        ✕
                  </button>
                                                    </div>
                                                  ) : (
                                                    <div style={{ display: 'flex', gap: 4 }}>
                                                      <select
                                                        key={`group-select-${unassignedGroups.size}-${selectableGroups.length}`}
                                                        value={quickEdit.group || ''}
                                                        onChange={(e) => updateQuickEditField('group', e.target.value)}
                                                        disabled={quickEditSaving}
                                                        style={{ flex: 1, padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
                                                      >
                                                        <option value="">No Group</option>
                                                        {selectableGroups.map(group => (
                                                          <option key={group.value} value={group.value}>{group.label}</option>
                                                        ))}
                                                      </select>
                                                      {quickEdit.group && (
                                                        <>
                                                          <button
                                                            className="btn btn-ghost btn-sm"
                                                            onClick={() => setRenamingGroup({ oldName: quickEdit.group, newName: quickEdit.group })}
                                                            disabled={quickEditSaving}
                                                            style={{ padding: '4px 8px', fontSize: 11, minWidth: 'auto' }}
                                                            title="Rename group"
                                                          >
                                                            ✏️
                                                          </button>
                                                          <button
                                                            className="btn btn-ghost btn-sm"
                                                            onClick={() => setEditingGroupColor({ groupName: quickEdit.group, subGroupName: null, color: getGroupColor(quickEdit.group) })}
                                                            disabled={quickEditSaving}
                                                            style={{ padding: '4px 8px', fontSize: 11, minWidth: 'auto' }}
                                                            title="Change color"
                                                          >
                                                            🎨
                                                          </button>
                                                          <button
                                                            className="btn btn-ghost btn-sm"
                                                            onClick={() => handleDeleteGroup(quickEdit.group)}
                                                            disabled={quickEditSaving}
                                                            style={{ padding: '4px 8px', fontSize: 11, minWidth: 'auto', color: '#dc2626' }}
                                                            title="Delete group"
                                                          >
                                                            🗑️
                                                          </button>
                                                        </>
                                                      )}
                                                    </div>
                                                  )}
                                                  {renamingGroup && renamingGroup.oldName === quickEdit.group && (
                                                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                                      <input
                                                        type="text"
                                                        value={renamingGroup.newName}
                                                        onChange={(e) => setRenamingGroup({ ...renamingGroup, newName: e.target.value })}
                                                        onKeyDown={(e) => {
                                                          if (e.key === 'Enter') {
                                                            handleRenameGroup(renamingGroup.oldName, renamingGroup.newName)
                                                          } else if (e.key === 'Escape') {
                                                            setRenamingGroup(null)
                                                          }
                                                        }}
                                                        autoFocus
                                                        style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: '1px solid #fb923c', fontSize: 12 }}
                                                      />
                                                      <button
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() => handleRenameGroup(renamingGroup.oldName, renamingGroup.newName)}
                                                        style={{ padding: '4px 8px', fontSize: 11 }}
                                                      >
                                                        ✓
                                                      </button>
                                                      <button
                                                        className="btn btn-ghost btn-sm"
                                                        onClick={() => setRenamingGroup(null)}
                                                        style={{ padding: '4px 8px', fontSize: 11 }}
                                                      >
                                                        ✕
                                                      </button>
                                                    </div>
                                                  )}
                                                </div>
                                                {quickEdit.group && quickEditGroupEntry && (
                                                  <div style={{ minWidth: 200 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Sub-Group</label>
                                                      <button
                                                        className="btn btn-ghost btn-sm"
                                                        onClick={() => {
                                                          setCreatingSubGroup(quickEdit.group)
                                                          setNewSubGroupName('')
                                                        }}
                                                        disabled={quickEditSaving}
                                                        style={{ padding: '2px 6px', fontSize: 10, minWidth: 'auto' }}
                                                        title="Create new sub-group"
                                                      >
                                                        + New
                                                      </button>
                                                    </div>
                                                    {creatingSubGroup === quickEdit.group ? (
                                                      <div style={{ display: 'flex', gap: 4 }}>
                                                        <input
                                                          type="text"
                                                          value={newSubGroupName}
                                                          onChange={(e) => setNewSubGroupName(e.target.value)}
                                                          onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                              handleCreateSubGroup(quickEdit.group)
                                                            } else if (e.key === 'Escape') {
                                                              setCreatingSubGroup(null)
                                                              setNewSubGroupName('')
                                                            }
                                                          }}
                                                          placeholder="Sub-group name..."
                                                          autoFocus
                                                          style={{ flex: 1, padding: '6px 12px', borderRadius: 6, border: '1px solid #c084fc', fontSize: 13 }}
                                                        />
                                                        <button
                                                          className="btn btn-primary btn-sm"
                                                          onClick={() => handleCreateSubGroup(quickEdit.group)}
                                                          style={{ padding: '6px 12px', fontSize: 11 }}
                                                        >
                                                          ✓
                                                        </button>
                                                        <button
                                                          className="btn btn-ghost btn-sm"
                                                          onClick={() => {
                                                            setCreatingSubGroup(null)
                                                            setNewSubGroupName('')
                                                          }}
                                                          style={{ padding: '6px 12px', fontSize: 11 }}
                                                        >
                                                          ✕
                                                        </button>
                                                      </div>
                                                    ) : (
                                                      <div style={{ display: 'flex', gap: 4 }}>
                                                        <select
                                                          key={`subgroup-select-${quickEdit.group}-${unassignedSubGroups.get(quickEdit.group)?.size || 0}-${quickEditSubGroupOptions.length}`}
                                                          value={quickEdit.subGroup || ''}
                                                          onChange={(e) => updateQuickEditField('subGroup', e.target.value)}
                                                          disabled={quickEditSaving || quickEditSubGroupOptions.length === 0}
                                                          style={{ flex: 1, padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}
                                                        >
                                                          <option value="">No Sub-Group</option>
                                                          {quickEditSubGroupOptions.map(sub => (
                                                            <option key={sub.name} value={sub.name}>{sub.name} ({sub.count})</option>
                                                          ))}
                                                        </select>
                                                        {quickEdit.subGroup && (
                                                          <>
                                                            <button
                                                              className="btn btn-ghost btn-sm"
                                                              onClick={() => setRenamingSubGroup({ groupName: quickEdit.group, oldName: quickEdit.subGroup, newName: quickEdit.subGroup })}
                                                              disabled={quickEditSaving}
                                                              style={{ padding: '4px 8px', fontSize: 11, minWidth: 'auto' }}
                                                              title="Rename sub-group"
                                                            >
                                                              ✏️
                                                            </button>
                                                            <button
                                                              className="btn btn-ghost btn-sm"
                                                              onClick={() => setEditingGroupColor({ groupName: quickEdit.group, subGroupName: quickEdit.subGroup, color: getGroupColor(`${quickEdit.group}::${quickEdit.subGroup}`) })}
                                                              disabled={quickEditSaving}
                                                              style={{ padding: '4px 8px', fontSize: 11, minWidth: 'auto' }}
                                                              title="Change color"
                                                            >
                                                              🎨
                                                            </button>
                                                            <button
                                                              className="btn btn-ghost btn-sm"
                                                              onClick={() => handleDeleteSubGroup(quickEdit.group, quickEdit.subGroup)}
                                                              disabled={quickEditSaving}
                                                              style={{ padding: '4px 8px', fontSize: 11, minWidth: 'auto', color: '#dc2626' }}
                                                              title="Delete sub-group"
                                                            >
                                                              🗑️
                                                            </button>
                                                          </>
                                                        )}
                                                      </div>
                                                    )}
                                                    {renamingSubGroup && renamingSubGroup.groupName === quickEdit.group && renamingSubGroup.oldName === quickEdit.subGroup && (
                                                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                                        <input
                                                          type="text"
                                                          value={renamingSubGroup.newName}
                                                          onChange={(e) => setRenamingSubGroup({ ...renamingSubGroup, newName: e.target.value })}
                                                          onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                              handleRenameSubGroup(renamingSubGroup.groupName, renamingSubGroup.oldName, renamingSubGroup.newName)
                                                            } else if (e.key === 'Escape') {
                                                              setRenamingSubGroup(null)
                                                            }
                                                          }}
                                                          autoFocus
                                                          style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: '1px solid #fb923c', fontSize: 12 }}
                                                        />
                                                        <button
                                                          className="btn btn-primary btn-sm"
                                                          onClick={() => handleRenameSubGroup(renamingSubGroup.groupName, renamingSubGroup.oldName, renamingSubGroup.newName)}
                                                          style={{ padding: '4px 8px', fontSize: 11 }}
                                                        >
                                                          ✓
                                                        </button>
                                                        <button
                                                          className="btn btn-ghost btn-sm"
                                                          onClick={() => setRenamingSubGroup(null)}
                                                          style={{ padding: '4px 8px', fontSize: 11 }}
                                                        >
                                                          ✕
                                                        </button>
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <button 
                                                    className="btn btn-ghost btn-sm"
                                                    onClick={closeQuickEdit}
                                                    disabled={quickEditSaving}
                                                    style={{ padding: '6px 12px' }}
                                                  >
                                                    Cancel
                                                  </button>
                                                  <button
                                                    className="btn btn-primary btn-sm"
                                                    onClick={handleQuickEditSave}
                                                    disabled={quickEditSaving}
                                                    style={{ padding: '6px 12px' }}
                                                  >
                                                    {quickEditSaving ? 'Saving…' : 'Save'}
                                                  </button>
                                                </div>
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    )
                                  })}
                                  </tbody>
                                </table>
                </div>
              )}

                          {!hasFilteredLots && (
                            <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
                              No lots match the selected filters.
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )
              })()}

              {/* Group Filter Tabs */}
              {onlyLivePlans && (() => {
                const availableGroups = getAvailableGroups()
                const othersCount = getOthersCount()
                const hasGroups = availableGroups.length > 0 || othersCount > 0
                
                if (!hasGroups) return null
                
                return (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                      <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                        Filter by Sales Group:
                      </label>
                      {selectedGroupFilter && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setSelectedGroupFilter(null)}
                          style={{ padding: '4px 12px', fontSize: 12 }}
                        >
                          Clear Filter
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <button
                        className={`tab-btn ${!selectedGroupFilter ? 'active' : ''}`}
                        onClick={() => setSelectedGroupFilter(null)}
                        style={{ 
                          padding: '8px 16px',
                          borderRadius: 6,
                          border: '1px solid #e5e7eb',
                          background: !selectedGroupFilter ? '#3b82f6' : '#fff',
                          color: !selectedGroupFilter ? '#fff' : '#374151',
                          fontSize: 14,
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        All Groups
                      </button>
                      {availableGroups.map((g, idx) => {
                        const isSelected = selectedGroupFilter && 
                                          selectedGroupFilter.stageName === g.stageName && 
                                          selectedGroupFilter.groupName === g.groupName
                        return (
                          <button
                            key={idx}
                            className={`tab-btn ${isSelected ? 'active' : ''}`}
                            onClick={() => setSelectedGroupFilter({ stageName: g.stageName, groupName: g.groupName, subGroupName: null })}
                            style={{ 
                              padding: '8px 16px',
                              borderRadius: 6,
                              border: '1px solid #e5e7eb',
                              background: isSelected ? '#3b82f6' : '#fff',
                              color: isSelected ? '#fff' : '#374151',
                              fontSize: 14,
                              fontWeight: 500,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}
                            title={`Stage: ${g.stageName}`}
                          >
                            <span>{g.groupName}</span>
                            <span style={{ 
                              fontSize: 12, 
                              opacity: 0.8,
                              background: isSelected ? 'rgba(255,255,255,0.2)' : '#f3f4f6',
                              padding: '2px 6px',
                              borderRadius: 4
                            }}>
                              {g.count}
                            </span>
                          </button>
                        )
                      })}
                      {othersCount > 0 && (
                        <button
                          className={`tab-btn ${selectedGroupFilter?.groupName === 'OTHERS' ? 'active' : ''}`}
                          onClick={() => setSelectedGroupFilter({ stageName: null, groupName: 'OTHERS', subGroupName: null })}
                          style={{ 
                            padding: '8px 16px',
                            borderRadius: 6,
                            border: '1px solid #e5e7eb',
                            background: selectedGroupFilter?.groupName === 'OTHERS' ? '#6b7280' : '#fff',
                            color: selectedGroupFilter?.groupName === 'OTHERS' ? '#fff' : '#374151',
                            fontSize: 14,
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}
                        >
                          <span>Others (No Group)</span>
                          <span style={{ 
                            fontSize: 12, 
                            opacity: 0.8,
                            background: selectedGroupFilter?.groupName === 'OTHERS' ? 'rgba(255,255,255,0.2)' : '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: 4
                          }}>
                            {othersCount}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* Lot Details Table - Only show when onlyLivePlans (Available lots page) */}
              {onlyLivePlans && (
                (selectedMapForLots.regions?.filter(r => r.name && String(r.name).trim() !== '' && r.status === 'AVAILABLE').length || 0) === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 8 }}>No available lots yet</div>
                </div>
              ) : (
                <>
                  {/* Search Bar */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ position: 'relative', maxWidth: 400 }}>
                      <input
                        type="text"
                        placeholder="Search lots by number, stage, group, size, price..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px 10px 38px',
                          fontSize: 14,
                          border: '1px solid #d1d5db',
                          borderRadius: 6,
                          outline: 'none',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      />
                      <svg
                        style={{
                          position: 'absolute',
                          left: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 18,
                          height: 18,
                          color: '#9ca3af'
                        }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm('')}
                          style={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            padding: 4,
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            color: '#9ca3af',
                            fontSize: 18,
                            lineHeight: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          aria-label="Clear search"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: '10%' }}>Lot Number</th>
                        <th style={{ width: '14%' }}>Stage Name</th>
                        <th style={{ width: '11%' }}>Sales Group</th>
                        <th style={{ width: '11%' }}>Sub-Group</th>
                        <th style={{ width: '10%' }}>Size</th>
                        <th style={{ width: '10%' }}>Price</th>
                        <th style={{ width: '9%' }}>Status</th>
                        <th style={{ width: '9%' }}>Frontage</th>
                        <th style={{ width: '9%' }}>Aspect</th>
                        <th style={{ width: '7%', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedMapForLots.regions
                        ?.filter(region => {
                          const isStaged = region.name && String(region.name).trim() !== ''
                          
                          // Apply status filter
                          let statusMatch = true
                          if (onlyLivePlans) {
                            statusMatch = isStaged && region.status === 'AVAILABLE'
                          } else if (activeLotTab === 'all') {
                            statusMatch = isStaged
                          } else if (activeLotTab === 'available') {
                            statusMatch = isStaged && region.status === 'AVAILABLE'
                          } else if (activeLotTab === 'sold') {
                            statusMatch = isStaged && region.status === 'SOLD'
                          } else if (activeLotTab === 'locked') {
                            statusMatch = isStaged && region.status === 'LOCKED'
                          } else {
                            statusMatch = isStaged
                          }
                          
                          if (!statusMatch) return false
                          
                          // Apply group filter if selected
                          if (selectedGroupFilter) {
                            if (selectedGroupFilter.groupName === 'OTHERS') {
                              return !region.customProps?.group
                            }

                            if (selectedGroupFilter.stageName && region.name !== selectedGroupFilter.stageName) {
                              return false
                            }

                            if (region.customProps?.group !== selectedGroupFilter.groupName) {
                              return false
                            }

                            if (selectedGroupFilter.subGroupName) {
                              return region.customProps?.subGroup === selectedGroupFilter.subGroupName
                            }
                          }
                          
                          // Apply search filter
                          if (searchTerm.trim() !== '') {
                            const term = searchTerm.toLowerCase()
                            
                            // Search in lot number
                            if (region.lotNumber && region.lotNumber.toLowerCase().includes(term)) return true
                            
                            // Search in stage name
                            if (region.name && region.name.toLowerCase().includes(term)) return true
                            
                            // Search in group
                            if (region.customProps?.group && region.customProps.group.toLowerCase().includes(term)) return true
                            
                            // Search in sub-group
                            if (region.customProps?.subGroup && region.customProps.subGroup.toLowerCase().includes(term)) return true
                            
                            // Search in size
                            if (region.lotSize && region.lotSize.toLowerCase().includes(term)) return true
                            
                            // Search in price
                            if (region.price && (String(region.price).includes(term) || `$${Number(region.price).toLocaleString()}`.toLowerCase().includes(term))) return true
                            
                            // Search in frontage
                            if (region.customProps?.frontage && region.customProps.frontage.toLowerCase().includes(term)) return true
                            
                            // Search in aspect
                            if (region.customProps?.aspect && region.customProps.aspect.toLowerCase().includes(term)) return true
                            
                            // Search in status
                            if (region.status && region.status.toLowerCase().includes(term)) return true
                            
                            return false
                          }
                          
                          return true
                        })
                        .map((region, index) => (
                          <tr key={region.id || index}>
                            <td data-label="Lot Number">
                              <div className="table-primary" style={{ fontWeight: 600 }}>
                                {region.lotNumber || `Lot ${index + 1}`}
                              </div>
                            </td>
                            <td data-label="Stage Name">
                              <div className="muted" style={{ fontSize: 14 }}>
                                {region.lotName || region.name || '—'}
                              </div>
                            </td>
                            <td data-label="Sales Group">
                              <div className="muted" style={{ fontSize: 14 }}>
                                {region.customProps?.group ? (
                                  <span style={{ 
                                    display: 'inline-block', 
                                    padding: '2px 8px', 
                                    borderRadius: 4, 
                                    background: '#f3e8ff', 
                                    color: '#7c3aed',
                                    fontSize: 12,
                                    fontWeight: 500
                                  }}>
                                    {region.customProps.group}
                                  </span>
                                ) : '—'}
                              </div>
                            </td>
                            <td data-label="Sub-Group">
                              <div className="muted" style={{ fontSize: 14 }}>
                                {region.customProps?.subGroup ? (
                                  <span style={{ 
                                    display: 'inline-block', 
                                    padding: '2px 8px', 
                                    borderRadius: 4, 
                                    background: '#e0f2fe', 
                                    color: '#0369a1',
                                    fontSize: 12,
                                    fontWeight: 500
                                  }}>
                                    {region.customProps.subGroup}
                                  </span>
                                ) : '—'}
                              </div>
                            </td>
                            <td data-label="Size">
                              <div className="muted" style={{ fontSize: 14 }}>
                                {region.lotSize || '—'}
                              </div>
                            </td>
                            <td data-label="Price">
                              <div className="muted" style={{ fontSize: 14 }}>
                                {region.price ? `$${Number(region.price).toLocaleString()}` : '—'}
                              </div>
                            </td>
                            <td data-label="Status" style={{ textAlign: 'left' }}>
                              {region.status === 'AVAILABLE' ? (
                                <span className="badge badge-success">
                                  <span className="dot dot-success"></span> AVAILABLE
                                </span>
                              ) : region.status === 'SOLD' ? (
                                <span className="badge badge-danger">
                                  <span className="dot dot-danger"></span> SOLD
                                </span>
                              ) : region.status === 'LOCKED' ? (
                                <span className="badge">
                                  <span className="dot dot-warning"></span> LOCKED
                                </span>
                              ) : (
                                <span className="badge">
                                  <span className="dot"></span> {region.status || 'UNKNOWN'}
                                </span>
                              )}
                            </td>
                            <td data-label="Frontage">
                              <div className="muted" style={{ fontSize: 12 }}>
                                {region.customProps?.frontage || '—'}
                              </div>
                            </td>
                            <td data-label="Aspect">
                              <div className="muted" style={{ fontSize: 12 }}>
                                {region.customProps?.aspect || '—'}
                              </div>
                            </td>
                          <td data-label="Actions" style={{ textAlign: 'right' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => openQuickEdit(region)}
                                style={{ padding: '4px 8px' }}
                                title="Quick edit"
                                aria-label="Quick edit lot"
                                disabled={quickEditSaving && quickEdit?.regionId === region.id}
                              >
                                ⚡
                              </button>
                            <button 
                              className="btn btn-sm"
                              onClick={() => {
                                  setQuickEdit(null)
                                const actualIndex = selectedMapForLots.regions.findIndex(r => r.id === region.id)
                                setEditModal({ map: selectedMapForLots, regionIndex: actualIndex })
                              }}
                              style={{ padding: '4px 8px', minWidth: 'auto' }}
                              title="Edit lot details"
                            >
                              ✏️
                            </button>
                            </div>
                          </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                </>
                )
              )}
            </div>
          )}

          {currentView === 'upload' && (
            <div>
              <div className="row" style={{ alignItems: 'center', marginBottom: 16 }}>
                <button className="btn" onClick={handleBackToList} style={{ background: '#e5e7eb' }}>← Back to Maps</button>
                <h3 className="title" style={{ marginLeft: 12 }}>Upload New Map</h3>
              </div>
              <MapUpload onMapCreated={handleMapCreated} />
            </div>
          )}

          {currentView === 'editor' && selectedMap && (
            <div>
              <div className="glass glass-card" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <button className="btn btn-ghost" onClick={handleBackToList}>← Back to Maps</button>
                <h3 className="title" style={{ margin: 0, marginLeft: 'auto' }}>Edit Map: {selectedMap.name}</h3>
              </div>
              <MapEditor map={selectedMap} onMapUpdated={fetchMaps} onPublished={() => setCurrentView('list')} />
            </div>
          )}

        {editModal && (
          <div className="modal-overlay" role="dialog" aria-modal="true">
            <div className="modal" style={{ maxWidth: '95vw', width: '95vw', height: '90vh', display: 'flex', flexDirection: 'column' }}>
              <div className="modal-header">
                <div className="modal-title">Edit Lot Details — {editModal.map?.name}</div>
                <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setEditModal(null)}>✕</button>
              </div>
              <div className="modal-body" style={{ display: 'flex', gap: 12, height: '100%', overflow: 'auto' }}>
                <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                  <MapEditor map={editModal.map} onMapUpdated={fetchMaps} embedded initialIndex={editModal.regionIndex} focusSelectedOnly />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Modal */}
        {exportModal !== null && (
          <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setExportModal(null)}>
            <div className="modal" style={{ maxWidth: '500px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">Export Lot Details</div>
                <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setExportModal(null)}>✕</button>
              </div>
              <div className="modal-body" style={{ padding: 20 }}>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Select Export Type:</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <button
                      className="btn"
                      onClick={() => setExportModal({ type: 'default', selectedGroup: null })}
                      style={{ 
                        textAlign: 'left', 
                        justifyContent: 'flex-start',
                        background: exportModal.type === 'default' ? '#3b82f6' : '#fff',
                        color: exportModal.type === 'default' ? '#fff' : '#374151',
                        border: `1px solid ${exportModal.type === 'default' ? '#3b82f6' : '#d1d5db'}`,
                        fontWeight: exportModal.type === 'default' ? 600 : 400
                      }}
                    >
                      📊 Default Export (All lots)
                    </button>
                    <button
                      className="btn"
                      onClick={() => setExportModal({ type: 'group', selectedGroup: null })}
                      style={{ 
                        textAlign: 'left', 
                        justifyContent: 'flex-start',
                        background: exportModal.type === 'group' ? '#3b82f6' : '#fff',
                        color: exportModal.type === 'group' ? '#fff' : '#374151',
                        border: `1px solid ${exportModal.type === 'group' ? '#3b82f6' : '#d1d5db'}`,
                        fontWeight: exportModal.type === 'group' ? 600 : 400
                      }}
                    >
                      📋 Export by Sales Groups (Include stage & sales group columns)
                    </button>
                  </div>
                </div>

                {exportModal.type === 'default' && (
                  <div style={{ marginTop: 16, padding: '12px', background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd' }}>
                    <div style={{ fontSize: 13, color: '#0369a1' }}>
                      Will export all lots with standard columns (Lot Number, Stage Name, Size, Price, Status, Frontage, Aspect)
                    </div>
                  </div>
                )}

                {exportModal.type === 'group' && (() => {
                  const availableGroups = getAvailableGroups()
                  const hasOthersLots = getOthersCount() > 0
                  const selectedGroupEntry = exportModal.selectedGroup && exportModal.selectedGroup.groupName !== 'OTHERS'
                    ? availableGroups.find(g => g.stageName === exportModal.selectedGroup.stageName && g.groupName === exportModal.selectedGroup.groupName)
                    : null
                  
                  return (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Select Export Option:</div>
                      <select
                        value={exportModal.selectedGroup ? (exportModal.selectedGroup.groupName === 'OTHERS' ? 'OTHERS' : `${exportModal.selectedGroup.stageName}::${exportModal.selectedGroup.groupName}`) : ''}
                        onChange={(e) => {
                          if (e.target.value === 'OTHERS') {
                            setExportModal({ ...exportModal, selectedGroup: { stageName: null, groupName: 'OTHERS', subGroupName: null } })
                          } else if (e.target.value) {
                            const [stageName, groupName] = e.target.value.split('::')
                            setExportModal({ ...exportModal, selectedGroup: { stageName, groupName, subGroupName: null } })
                          } else {
                            setExportModal({ ...exportModal, selectedGroup: null })
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: 6,
                          border: '1px solid #c084fc',
                          background: '#fff',
                          fontSize: 14,
                          marginBottom: selectedGroupEntry && selectedGroupEntry.subGroups.length > 0 ? 12 : 0
                        }}
                      >
                        <option value="">All Groups (including others)</option>
                        {availableGroups.map((g, idx) => (
                          <option key={idx} value={`${g.stageName}::${g.groupName}`}>
                            Stage {g.stageName} - Sales Group {g.groupName} ({g.count} lots)
                          </option>
                        ))}
                        {hasOthersLots && (
                          <option value="OTHERS">Others (No Sales Group)</option>
                        )}
                      </select>
                      
                      {selectedGroupEntry && selectedGroupEntry.subGroups.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#374151' }}>Select Sub-Group (optional):</div>
                          <select
                            value={exportModal.selectedGroup?.subGroupName || ''}
                            onChange={(e) => {
                              setExportModal({
                                ...exportModal,
                                selectedGroup: {
                                  ...exportModal.selectedGroup,
                                  subGroupName: e.target.value || null
                                }
                              })
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              borderRadius: 6,
                              border: '1px solid #c084fc',
                              background: '#fff',
                              fontSize: 14
                            }}
                          >
                            <option value="">All Sub-Groups in {selectedGroupEntry.groupName}</option>
                            {selectedGroupEntry.subGroups.map((sub, idx) => (
                              <option key={idx} value={sub.name}>
                                {sub.name} ({sub.count} lots)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      {exportModal.selectedGroup && (
                        <div style={{ marginTop: 12, fontSize: 12, color: '#7c3aed', padding: '8px 12px', background: '#faf5ff', borderRadius: 6 }}>
                          {exportModal.selectedGroup.groupName === 'OTHERS' ? (
                            <>Will export only: Lots without any sales group</>
                          ) : exportModal.selectedGroup.subGroupName ? (
                            <>Will export only: Stage {exportModal.selectedGroup.stageName} - Sales Group {exportModal.selectedGroup.groupName} - Sub-Group {exportModal.selectedGroup.subGroupName}</>
                          ) : (
                            <>Will export only: Stage {exportModal.selectedGroup.stageName} - Sales Group {exportModal.selectedGroup.groupName} (all sub-groups)</>
                          )}
                        </div>
                      )}
                      {!exportModal.selectedGroup && (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                          Will export all lots with stage and sales group columns
                        </div>
                      )}
                    </div>
                  )
                })()}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                  <button className="btn btn-ghost" onClick={() => setExportModal(null)}>
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={handleExportConfirm}
                    disabled={exportModal.type === null}
                  >
                    Export
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Color Picker Modal */}
        {editingGroupColor && (
          <div className="modal-overlay" onClick={() => setEditingGroupColor(null)} style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0, 0, 0, 0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000 
          }}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500, width: '90%', background: '#fff', borderRadius: 8, padding: 24 }}>
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  Change Color
                </h3>
                <p style={{ margin: '8px 0 0', fontSize: 14, color: '#6b7280' }}>
                  {editingGroupColor.subGroupName 
                    ? `Select a color for "${editingGroupColor.groupName} > ${editingGroupColor.subGroupName}"`
                    : `Select a color for "${editingGroupColor.groupName}"`}
                </p>
        </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                  Current Color:
                </label>
                <div style={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: 8, 
                  background: editingGroupColor.color,
                  border: '2px solid #e5e7eb',
                  marginBottom: 16
                }} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                  Select from Palette:
                </label>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(6, 1fr)', 
                  gap: 8 
                }}>
                  {groupColorPalette.map((color) => (
                    <button
                      key={color}
                      className="btn"
                      onClick={() => handleChangeGroupColor(editingGroupColor.groupName, color, editingGroupColor.subGroupName || null)}
                      style={{
                        width: '100%',
                        aspectRatio: '1',
                        background: color,
                        border: editingGroupColor.color === color ? '3px solid #000' : '2px solid #e5e7eb',
                        borderRadius: 8,
                        cursor: 'pointer',
                        position: 'relative'
                      }}
                      title={color}
                    >
                      {editingGroupColor.color === color && (
                        <span style={{ 
                          position: 'absolute', 
                          top: '50%', 
                          left: '50%', 
                          transform: 'translate(-50%, -50%)',
                          fontSize: 20,
                          color: '#fff',
                          textShadow: '0 0 4px rgba(0,0,0,0.5)'
                        }}>✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                  Or enter custom hex color:
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="text"
                    value={editingGroupColor.color}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value.match(/^#[0-9A-Fa-f]{0,6}$/)) {
                        setEditingGroupColor({ ...editingGroupColor, color: value })
                      }
                    }}
                    placeholder="#FF0000"
                    style={{ 
                      flex: 1, 
                      padding: '8px 12px', 
                      borderRadius: 6, 
                      border: '1px solid #d1d5db', 
                      fontSize: 14 
                    }}
                  />
                  <div style={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: 6, 
                    background: editingGroupColor.color.match(/^#[0-9A-Fa-f]{6}$/) ? editingGroupColor.color : '#e5e7eb',
                    border: '2px solid #d1d5db',
                    flexShrink: 0
                  }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button 
                  className="btn btn-ghost" 
                  onClick={() => setEditingGroupColor(null)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={() => {
                    if (editingGroupColor.color.match(/^#[0-9A-Fa-f]{6}$/)) {
                      handleChangeGroupColor(editingGroupColor.groupName, editingGroupColor.color, editingGroupColor.subGroupName || null)
                    } else {
                      alert.warning('Please enter a valid hex color (e.g., #FF0000)')
                    }
                  }}
                >
                  Apply Color
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  )
}

